<template>
    <div
        v-if="apiFailed || maintenanceMode || (str && chatUser && (chatUser.id !== newClientKey || (chatUser.id === newClientKey && rooms.length)))"
        v-show="loaded"
        class="chat-container"
        v-bind:class="{'chat-container-single-room': singleRoom}"
    >
        <div v-if="apiFailed" class="app-error-info">
            <img src="../assets/images/icon_server_down.svg" />
            <div>
                Parece que estamos a enfrentar alguns problemas ao estabelecer ligação com o servidor. Por favor, verifica a ligação à internet ou aguarda um pouco antes de tentar novamente.
            </div>
        </div>

        <div v-if="maintenanceMode" class="app-error-info">
            {{ str['chat_maintenance_mode'] }}
        </div>

        <div v-if="chatUser && !chatUser.chat_id" class="start-chat-button">
            Por favor contactar o suporte.
        </div>

        <div v-if="chatUser && chatUser.chat_id">
            <vue-advanced-chat
                ref="chatWindowRef"
                id="app-chat-window"
                :height="'100vh'"
                :theme="theme"
                :current-user-id="chatUser.chat_id.toString()"
                :rooms="JSON.stringify(vueAdvancedChatRooms)"
                :rooms-loaded="roomsLoaded"
                :loading-rooms="loadingRooms"
                :load-first-room="loadFirstRoom"
                :messages="JSON.stringify(vueAdvancedChatMessages)"
                :messages-loaded="messagesLoaded"
                :message-actions="JSON.stringify(messageActions)"
                show-emojis="true"
                show-reaction-emojis="false"
                message-reactions="false"
                :text-messages="JSON.stringify(textMessages)"
                :show-audio="showAudio"
                :single-room="singleRoom"
                :accepted-files="acceptedFiles"
                :room-message="roomDefaultMessage"
                @fetch-messages="onFetchMessages($event.detail[0])"
                @send-message="onSendMessage($event.detail[0])"
                @edit-message="onEditMessage($event.detail[0])"
                @delete-message="onDeleteMessageConfirmation($event.detail[0])"
                @fix-message="onFixMessage($event.detail[0])"
                @save-feedback-message="onSaveFeedbackMessage($event.detail[0])"
                @open-file="onOpenFile($event.detail[0])"
                @preview-file="onPreviewFile($event.detail[0])"
                @message-action-handler="onMessageActionHandler($event.detail[0])"
                @toggle-rooms-list="onToggleRoomsList()"
                @typing-message="onTypingMessage($event.detail[0])"
            >
                <div slot="rooms-list-search">
                    <div class="rooms-search">
                        <div class="rooms-search-input">
                            <span class="rooms-version-label">
                                v{{ appVersion }}
                            </span>
                            <input v-model="roomsSearchValue" :placeholder="str['search_clients']"
                                @input="onChangeSearchInput" v-on:keyup.enter="applySearchRoomsValue" />
                        </div>
                        <div class="rooms-search-button">
                            <v-btn @click="applySearchRoomsValue"
                                class="rooms-search-button-element"
                            >
                                <img src="../assets/images/icon_search.svg"
                                    class="rooms-search-button-search-image" />
                            </v-btn>
                        </div>
                        <div v-if="filtersEnabled" class="rooms-search-button">
                            <v-btn @click="openFilters"
                                class="rooms-search-button-element pt-clients-open"
                            >
                                <img src="../assets/images/icon_filter.svg"
                                    class="rooms-search-button-filter-image" />
                            </v-btn>
                        </div>
                        <div v-if="sendMessageToAllEnabled" class="rooms-search-button">
                            <v-btn @click="openSendMessageToAll"
                                class="rooms-search-button-element pt-clients-open"
                                v-bind:class="{'rooms-search-button-send-disabled': !filteredRooms.length}"
                            >
                                <img src="../assets/images/icon_send_message.svg"
                                    class="rooms-search-button-send-image" />
                            </v-btn>
                        </div>
                    </div>
                    <div v-if="roomsPaginationEnabled" class="rooms-pagination" :class="{'rooms-pagination-flex': pagesEmployeesEnabled}">
                        <div v-if="pagesEmployeesEnabled" class="rooms-pagination-employee">
                            {{ pagesEmployees && pagesEmployees[currentRoomsPageInput] && pagesEmployees[currentRoomsPageInput].length ? pagesEmployees[currentRoomsPageInput].join(', ') : '' }}
                        </div>
                        <div class="rooms-pagination-page">
                            <div class="rooms-pagination-page-arrow left">
                                <div @click="previousRoomsPage" class="pt-clients-open">
                                    <img src="../assets/images/icon_arrow_left.svg" />
                                </div>
                            </div>
                            <div class="rooms-pagination-page-value">
                                <input v-model="currentRoomsPageInput" v-on:keyup.enter="changeCurrentRoomsPage" />
                            </div>
                            <div class="rooms-pagination-page-arrow right">
                                <div @click="nextRoomsPage" class="pt-clients-open">
                                    <img src="../assets/images/icon_arrow_right.svg" />
                                </div>
                            </div>
                            <div style="color: #888; padding-left: 10px; min-width: 50px;">
                                | {{ cacheFilteredRooms.length }}
                            </div>
                        </div>
                    </div>
                    <div v-if="filterCategoriesEnabled && !archivedConversationsFilterActive" class="rooms-filter-tabs">
                        <div
                            v-for="(category, index) in chatCategories"
                            :key="index"
                            @click="selectCategoryFilter(category)"
                            class="rooms-filter-tabs-item"
                            :class="{'active': category.value === categoryFilterActive}"
                        >
                            <div class="rooms-filter-tabs-item-text">
                                {{ str[category.label] ? str[category.label] : category.label }}
                            </div>
                            <div
                                v-if="category.unreadMessages > 0"
                                class="warning--text rooms-filter-tabs-item-value"
                            >
                                ({{ category.unreadMessages > 999 ? '+999' : category.unreadMessages }})
                            </div>
                        </div>
                    </div>
                    <div v-if="filtersGroupsEnabled" class="rooms-filter-tabs">
                        <div
                            v-for="(filter, index) in filters"
                            v-if="filter.group"
                            :key="index"
                            @click="selectGroupFilter(filter)"
                            class="rooms-filter-tabs-item"
                            :class="{'active': filter.id === groupFilterActive}"
                        >
                            <div class="rooms-filter-tabs-item-text">
                                {{ str[filter.name] ? str[filter.name] : filter.name }}
                            </div>
                            <div
                                v-if="filter.unreadMessages > 0"
                                class="warning--text rooms-filter-tabs-item-value"
                            >
                                ({{ filter.unreadMessages > 999 ? '+999' : filter.unreadMessages }})
                            </div>
                        </div>
                    </div>
                </div>
                <div slot="room-header-info">
                    <div class="vac-info-wrapper">
                        <div class="vac-text-ellipsis">
                            <div class="vac-room-name vac-text-ellipsis">
                                <img
                                    v-if="showNotes && roomUser"
                                    @click="openNotes()"
                                    src="../assets/images/icon_info.svg"
                                    class="rooms-search-button-info"
                                />
                                {{ singleRoom ? str['chat'] : (getActiveRoom() ? (getActiveRoom().roomName + (roomUser && roomUser.country ? (' (' + roomUser.country + ')') : '')) : '') }}
                            </div>
                            <div
                                v-if="!singleRoom && roomUser && roomUser.notes && roomUser.notes.health_problems && roomUser.notes.health_problems.toLowerCase() !== 'não'"
                                @click="openNotes()"
                                class="text-ellipsis rooms-info-label cursor-hover"
                                style="max-width: 180px;"
                            >
                                {{ roomUser.notes.health_problems }}
                            </div>
                            <div class="vac-room-info vac-text-ellipsis rooms-info-label">
                                <div v-if="tagsEnabled && !singleRoom && getActiveRoom() && getActiveRoom().tags">
                                    <span
                                        v-for="tag in getActiveRoom().tags"
                                        :key="tag"
                                        style="margin-right: 5px;"
                                        :style="{color: tagsDict && tagsDict[tag] && tagsDict[tag].color}"
                                    >
                                        {{ tag }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="fixMessageEnabled && fixedMessages"
                        class="list-fixed-messages-items custom-scroll"
                    >
                        <div class="list-fixed-messages-items-list">
                            <div
                                v-for="(fixedMessage, fixedMessageIndex) in fixedMessages"
                                :key="fixedMessageIndex"
                                class="list-fixed-messages-item"
                                :class="{'list-fixed-messages-item-pt': chatUser.chat_id === fixedMessage.senderId}"
                            >
                                <div class="list-fixed-messages-item-title">
                                    <span>
                                        {{ fixedMessage.date }} {{ fixedMessage.timestamp }}
                                    </span>
                                    <img
                                        @click="unfixMessage(fixedMessage._id)"
                                        src="../assets/images/icon_unpin.svg"
                                        class="list-fixed-messages-item-icon"
                                    />
                                </div>
                                <div v-if="fixedMessage.type === filesTypes.text">
                                    {{ fixedMessage.content }}
                                </div>
                                <div
                                    v-if="fixedMessage.type !== filesTypes.text"
                                    class="list-fixed-messages-item-file"
                                >
                                    <v-btn
                                        color="primary"
                                        @click="openFixedMessage(fixedMessage)"
                                    >
                                        {{ str['open'] }}
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div slot="room-options">
                    <div class="room-options-actions">
                        <div v-if="showHasPlansEnabled && roomUser"
                            class="rooms-button-element rooms-button-icon"
                        >
                            <div class="rooms-button-element-image">
                                <img
                                    v-if="roomUser.has_training_plan"
                                    src="../assets/images/icon_train.svg"
                                    class="rooms-search-button-image-small"
                                />
                                <img
                                    v-if="roomUser.has_food_plan"
                                    src="../assets/images/icon_food.svg"
                                    class="rooms-search-button-image-small"
                                />
                            </div>
                        </div>
                        <div v-if="showSubscriptionEnabled && roomUser"
                            class="rooms-button-element rooms-button-icon"
                        >
                            <div class="rooms-button-element-image">
                                <img
                                    v-if="roomUser.paym_sub"
                                    src="../assets/images/icon_subscription_on.svg"
                                    class="rooms-search-button-image"
                                />
                                <img
                                    v-if="!roomUser.paym_sub"
                                    src="../assets/images/icon_subscription_off.svg"
                                    class="rooms-search-button-image"
                                />
                            </div>
                        </div>
                        <v-btn v-if="updateFeedbackDateEnabled && messages && messages.length"
                            @click="openPendingFeedback()"
                            class="rooms-button-element rooms-button-element-no-space warning--text"
                        >
                            <div class="rooms-button-element-image">
                                <img
                                    src="../assets/images/icon_feedback.svg"
                                    class="rooms-search-button-image"
                                />
                                <b v-if="pendingFeedbacksEnabled && pendingFeedbacks && pendingFeedbacks.length && getActiveRoom() && getActiveRoom().users[0] && pendingFeedbacks.indexOf(parseInt(getActiveRoom().users[0]._id)) > -1">
                                    {{ str['pending_physical_evaluation'] }}
                                </b>
                            </div>
                        </v-btn>
                        <v-btn v-if="messages && messages.length"
                            @click="loadRoomMoreMessages()"
                            class="rooms-button-element"
                        >
                            <img
                                src="../assets/images/icon_load_more.svg"
                                class="rooms-search-button-image"
                            />
                        </v-btn>
                        <v-menu
                            v-if="roomOptionsEnabled"
                            content-class="select-room-options"
                            offset-y
                        >
                            <template
                                v-slot:activator="{ on, attrs }"
                            >
                                <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="getRoomOptions()"
                                    class="select-room-options-btn"
                                >
                                    <img
                                        src="../assets/images/icon_menu.svg"
                                    />
                                </v-btn>
                            </template>
                            <v-list>
                                <div
                                    v-for="(item, index) in roomOptions"
                                    :key="index"
                                    @click="onSelectRoomOption(item.value)"
                                >
                                    <div>
                                        {{ item.label }}
                                    </div>
                                </div>
                            </v-list>
                        </v-menu>
                    </div>
                </div>
                <div v-for="room in filteredRooms" :slot="'room-list-avatar_' + room.roomId">
                    <div
                        v-if="room.fixed || room.lastMessage.distributed || room.lastMessage.seen || (filterCategoriesEnabled && room.categoryProcessing)"
                        style="display: flex; margin-right: 5px;"
                    >
                        <div v-if="filterCategoriesEnabled && room.categoryProcessing" class="loading-text">
                            IA
                        </div>
                        <div v-if="room.fixed" style="margin-right: 2px;">
                            <img src="../assets/images/icon_pin_on.svg" style="height: 14px;" />
                        </div>
                        <div v-if="chatUser && room.lastMessage.userId === chatUser.chat_id && room.lastMessage.distributed && !room.lastMessage.seen">
                            <img src="../assets/images/icon_distributed.svg" style="height: 14px;" />
                        </div>
                        <div v-if="chatUser && room.lastMessage.userId === chatUser.chat_id && room.lastMessage.seen">
                            <img src="../assets/images/icon_seen.svg" style="height: 14px;" />
                        </div>
                    </div>
                </div>
                <div
                    v-if="roomTypingEnabled || draftEnabled || (tagsEnabled && room.tags && room.tags.length)"
                    v-for="room in filteredRooms"
                    :slot="'room-list-options_' + room.roomId"
                >
                    <div
                        v-if="(roomTypingEnabled && room.opened) || (draftEnabled && room.draft) || (tagsEnabled && room.tags && room.tags.length)"
                        style="position: absolute; left: 10px; bottom: 3px; display: flex; align-items: center;"
                        class="list-separator"
                    >
                        <span
                            v-if="tagsEnabled && room.tags && room.tags.length"
                        >
                            <span
                                v-for="tag in room.tags"
                                :key="tag"
                                style="margin-right: 5px;"
                                :style="{color: tagsDict && tagsDict[tag] && tagsDict[tag].color}"
                            >
                                {{ tag }}
                            </span>
                        </span>
                        <span
                            v-if="roomTypingEnabled && room.opened"
                            class="text-ellipsis"
                            style="max-width: 140px;"
                        >
                            {{ str['chat_opened'] }}
                        </span>
                        <span
                            v-if="draftEnabled && room.draft && !room.opened"
                            class="text-ellipsis"
                            style="max-width: 140px;"
                        >
                            {{ str['draft'] + ': ' + room.draft }}
                        </span>
                    </div>
                </div>
            </vue-advanced-chat>
        </div>

        <div v-if="chatUser && filtersVisible" class="dialog-container">
            <div class="dialog-container-box">
                <div class="dialog-container-header">
                    {{ str['filters'] }}
                </div>
                <div class="dialog-container-body">
                    <div
                        v-for="(filter, index) in filters"
                        v-if="!filter.group"
                        :key="index"
                        class="dialog-container-body-filter"
                    >
                        <div class="dialog-container-body-filter-checkbox">
                            <v-checkbox
                                v-model="filter.value"
                                :label="str[filter.name] ? str[filter.name] : filter.name"
                                hide-details
                                class="pt-0"
                            ></v-checkbox>
                        </div>
                        <div v-if="filter.days && filter.value"
                            class="dialog-container-body-filter-days mt-2"
                        >
                            <div>
                                <div>
                                    {{ str['day_min'] }}
                                </div>
                                <div>
                                    <v-text-field
                                        v-model="filter.days[0]"
                                        :placeholder="str['day']"
                                        type="number"
                                        :min="0"
                                        :max="filter.days[1]"
                                    />
                                </div>
                            </div>
                            <div>
                                <div>
                                    {{ str['day_max'] }}
                                </div>
                                <div>
                                    <v-text-field
                                        v-model="filter.days[1]"
                                        :placeholder="str['day']"
                                        type="number"
                                        :min="filter.days[0]"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-if="filter.min && filter.value"
                            class="dialog-container-body-filter-min mt-2"
                        >
                            <v-text-field
                                v-model="filter.min.value"
                                :placeholder="str['min']"
                                type="number"
                                :min="0"
                                hide-details
                                outlined
                                rounded
                                class="pt-0 mt-0"
                            />
                        </div>
                   </div>
                </div>
                <div class="dialog-container-footer">
                    <v-btn @click="confirmFilters"
                        class="dialog-container-button-confirm"
                        :disabled="!validateFilters()"
                    >
                        {{ str['apply'] }}
                    </v-btn>
                    <v-btn @click="cancelFilters" class="dialog-container-button-cancel">
                        {{ str['clear'] }}
                    </v-btn>
                </div>
            </div>
        </div>

        <div v-if="chatUser && sendMessageToAllVisible" class="dialog-container">
            <div class="dialog-container-box">
                <div class="dialog-container-header">
                    {{ str['send_message_all'] }}
                </div>
                <div class="dialog-container-body">
                    <v-textarea
                        v-model="messageToAllValue"
                        autocomplete="new-value"
                        :name="Math.random()"
                        hide-details
                        outlined
                        rounded
                    ></v-textarea>
                </div>
                <div class="dialog-container-footer">
                    <v-btn @click="confirmSendMessageToAll"
                        class="dialog-container-button-confirm"
                        :disabled="!messageToAllValue ? true : false">
                        {{ str['send'] }}
                    </v-btn>
                    <v-btn @click="cancelSendMessageToAll" class="dialog-container-button-cancel">
                        {{ str['close'] }}
                    </v-btn>
                </div>
            </div>
        </div>

        <div v-if="chatUser && roomNotesVisible" class="dialog-container dialog-container-popover">
            <div class="dialog-container-box">
                <div class="dialog-container-header">
                    {{ str['notes'] }}
                </div>
                <div v-if="roomNotesVisible.data" class="dialog-container-body">
                    <div v-if="roomNotesVisible.data.accepted_foods">
                        <div class="dialog-room-notes-title success--text">
                            <b>{{ str['accepted_foods'] }}:</b>
                        </div>
                        <div v-for="fd in roomNotesVisible.data.accepted_foods">
                            <div>
                                • {{ fd }}
                            </div>
                        </div>
                        <div v-if="!roomNotesVisible.data.accepted_foods.length">
                            {{ str['foods_empty'] }}
                        </div>
                    </div>
                    <div v-if="roomNotesVisible.data.unaccepted_foods">
                        <div class="dialog-room-notes-title warning--text">
                            <b>{{ str['unaccepted_foods'] }}:</b>
                        </div>
                        <div v-for="fd in roomNotesVisible.data.unaccepted_foods">
                            <div>
                                • {{ fd }}
                            </div>
                        </div>
                        <div v-if="!roomNotesVisible.data.unaccepted_foods.length">
                            {{ str['foods_empty'] }}
                        </div>
                    </div>
                    <div v-if="roomNotesVisible.data.intolerant_foods">
                        <div class="dialog-room-notes-title warning--text">
                            <b>{{ str['intolerant_foods'] }}:</b>
                        </div>
                        <div v-for="fd in roomNotesVisible.data.intolerant_foods">
                            <div>
                                • {{ fd }}
                            </div>
                        </div>
                        <div v-if="!roomNotesVisible.data.intolerant_foods.length">
                            {{ str['foods_empty'] }}
                        </div>
                    </div>
                    <div v-if="roomNotesVisible.data.include_supplements">
                        <div class="dialog-room-notes-title">
                            <b class="warning--text">{{ str['include_supplements'] }}: </b>{{ roomNotesVisible.data.include_supplements }}
                        </div>
                    </div>
                    <div v-if="roomNotesVisible.data.health_problems">
                        <div class="dialog-room-notes-title dialog-room-notes-title-break">
                            <b class="warning--text">{{ str['health_problems'] }}: </b>{{ roomNotesVisible.data.health_problems }}
                        </div>
                    </div>
                </div>
                <div v-if="!roomNotesVisible.data" class="dialog-container-body">
                   <div class="dialog-room-notes-no-data">
                        {{ str['no_data'] }}
                   </div>
                </div>
                <div class="dialog-container-footer">
                    <v-btn @click="closeNotes" class="dialog-container-button-cancel">
                        {{ str['close'] }}
                    </v-btn>
                </div>
            </div>
        </div>
        <div v-if="chatUser && virtualKeyboardVisible">
            <Keyboard @onKeyPress="onKeyboardPress" />
        </div>

        <v-dialog
            v-if="chatUser && pendingFeedbacksEnabled && dialogPendingFeedback"
            v-model="dialogPendingFeedback"
            persistent
            max-width="500px"
        >
            <v-card>
                <v-card-title>
                    <span class="headline">
                        {{ str['update_feedback_date'] }}
                    </span>
                </v-card-title>
                <v-card-text v-if="pendingFeedbackClient">
                    <v-menu
                        v-model="pendingFeedbackMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="pendingFeedbackClient.feedback_day"
                                :label="str['feedback_date']"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="pendingFeedbackClient.feedback_day"
                            style="margin: 0;"
                            @change="pendingFeedbackMenu = false"
                            :locale="datepickerLanguage"
                        />
                    </v-menu>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeClientFeedback()"
                    >
                        {{ str['cancel'] }}
                    </v-btn>
                    <v-btn
                        color="success"
                        @click="updateClientFeedback()"
                    >
                        {{ str['confirm'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="chatUser && roomUpdateTagsEnabled"
            v-model="dialogUpdateTags"
            persistent
            max-width="400px"
        >
            <v-card>
                <v-card-title>
                    <span class="headline">
                        {{ str['tags'] }}
                    </span>
                </v-card-title>
                <v-card-text v-if="roomUpdateTagsData">
                    <v-autocomplete
                        class="mt-0 pt-0"
                        item-text="label"
                        item-value="value"
                        v-model="roomUpdateTagsData.value"
                        :items="roomUpdateTagsData.items"
                        :multiple="true"
                        :no-data-text="str['no_data']"
                        :search-input.sync="roomUpdateTagsData.searchInput"
                        @change="roomUpdateTagsData.searchInput=''"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeRoomTags()"
                    >
                        {{ str['cancel'] }}
                    </v-btn>
                    <v-btn
                        color="success"
                        @click="confirmRoomTags()"
                    >
                        {{ str['confirm'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="chatUser && IASummaryEnabled"
            v-model="dialogIASummary"
            persistent
            max-width="500px"
        >
            <v-card>
                <v-card-text v-if="dialogIASummaryContent">
                    <div v-if="dialogIASummaryContent.personalidade">
                        <div>
                            <b>
                                {{ str['personality'] }}
                            </b>
                        </div>
                        <div
                            v-for="(perItem, perItemIndex) in dialogIASummaryContent.personalidade"
                            :key="perItemIndex"
                        >
                            • {{ perItem }}
                        </div>
                    </div>
                    <div
                        v-if="dialogIASummaryContent.resumo"
                        class="mt-2"
                    >
                        <div>
                            <b>
                                {{ str['summary'] }}
                            </b>
                        </div>
                        <div
                            v-for="(resItem, resItemIndex) in dialogIASummaryContent.resumo"
                            :key="resItemIndex"
                        >
                            • {{ resItem }}
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        @click="openIASummary(true)"
                    >
                        {{ str['refresh'] }}
                    </v-btn>
                    <v-btn
                        color="default"
                        @click="closeIASummary()"
                    >
                        {{ str['close'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <VueDragResize
            v-if="chatUser && roomSummaryNotesEnabled && dialogRoomSummaryNotes"
            :isActive="true"
            :w="350"
            :h="350"
            :x="20"
            :y="50"
            class="rooms-box-float"
        >
            <v-card class="rooms-box-float-content">
                <v-card-text class="rooms-box-float-content-scroll custom-scroll">
                    <div>
                        <div class="rooms-box-float-content-title">
                            {{ str['last_physical_evaluation'] }}: {{ dialogRoomLastFeedbackDate }}
                        </div>
                    </div>
                    <div
                        v-for="item in dialogRoomSummaryNotes"
                        @mousedown="$event.stopPropagation()"
                    >
                        <div class="rooms-box-float-content-title">
                            {{ item.day }}
                        </div>
                        <div class="rooms-box-float-content-list">
                            <div
                                v-for="itemNotes in item.notes"
                            >
                                • {{ itemNotes }}
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeRoomSummaryNotes()"
                    >
                        {{ str['close'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </VueDragResize>

        <v-dialog
            v-if="chatUser && fixMessageEnabled"
            v-model="dialogFixedMessage"
            persistent
            max-width="500px"
        >
            <v-card v-if="dialogFixedMessageData">
                <v-card-title class="pb-0 text-center">
                    <h3>
                        {{ dialogFixedMessageData.date }}
                    </h3>
                </v-card-title>
                <v-card-text class="text-center dialog-fixed-message">
                    <div v-if="dialogFixedMessageData.type === filesTypes.text">
                        {{ dialogFixedMessageData.content }}
                    </div>
                    <div v-if="dialogFixedMessageData.type === filesTypes.image">
                        <img
                            :src="dialogFixedMessageData.files[0].url"
                        />
                    </div>
                    <div  v-if="dialogFixedMessageData.type === filesTypes.audio">
                        <audio controls>
                            <source
                                :src="dialogFixedMessageData.files[0].url"
                                type="audio/mp3"
                            >
                        </audio>
                    </div>
                    <div v-if="dialogFixedMessageData.type === filesTypes.video">
                        <video
                            :src="dialogFixedMessageData.files[0].url"
                            controls
                        />
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeFixedMessage()"
                    >
                        {{ str['close'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="newCalendarEventEnabled && dialogNewCalendarEvent"
            v-model="dialogNewCalendarEvent"
            max-width="600px"
        >
            <v-card>
                <v-card-title>
                    <span class="headline">
                        {{ str['new_event'] }}
                    </span>
                </v-card-title>
                <v-card-text class="dialog-card-content">
                    <div
                        v-for="item in newCalendarEventData"
                        :key="item.id"
                    >
                        <v-textarea
                            v-if="item.type === 'textarea'"
                            v-model="item.value"
                            :label="str[item.title] ? str[item.title] : item.title"
                            hide-details
                        />
                        <v-menu
                            v-if="item.type === 'datepicker'"
                            v-model="item.menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="item.value"
                                    v-bind:label="str[item.title] ? str[item.title] : item.title"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker
                                v-model="item.value"
                                style="margin: 0;"
                                @change="item.menu = false"
                                :locale="datepickerLanguage"
                            />
                        </v-menu>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeNewCalendarEventDialog()"
                    >
                        {{ str['cancel'] }}
                    </v-btn>
                    <v-btn
                        color="success"
                        @click="newCalendarEvent()"
                        :disabled="!newCalendarEventValidated()"
                    >
                        {{ str['save'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="dialogImages && dialogImagesData && dialogImagesData.images"
            v-model="dialogImages"
            max-width="800px"
        >
            <v-card>
                <v-card-title>
                    <span class="headline">
                        {{ str['images'] }}
                    </span>
                </v-card-title>
                <v-card-text class="dialog-card-content dialog-images custom-scroll">
                    <div>
                        <div
                            v-for="item in dialogImagesData.images"
                            :key="item.id"
                            class="dialog-images-item"
                        >
                            <div>
                                {{ item.date }}
                            </div>
                            <div>
                                <img :src="item.media" />
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!dialogImagesData.loadMoreDisabled"
                        class="dialog-images-button"
                    >
                        <v-btn
                            @click="getImagesMessages()"
                            :disabled="dialogImagesData.processing"
                        >   
                            <span v-if="!dialogImagesData.processing">
                                {{ str['show_more_images'] }}
                            </span>
                            <span v-if="dialogImagesData.processing">
                                {{ str['processing'] }}
                            </span>
                        </v-btn>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="default"
                        @click="closeImagesDialog()"
                    >
                        {{ str['close'] }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div
            v-if="loadingWaitActive"
            class="custom-loading-screen"
        >
            <div>
                <div>
                    <img src="../assets/images/loading.gif" />
                </div>
                <div>
                    {{ str['loading_wait'] }}
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .chat-container {
        position: relative;
    }

    .custom-loading-screen {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 16px;
        background-color: rgba(0, 0, 0, 0.8);
        color: #fff;
        z-index: 99999;
    }

    .custom-loading-screen img {
        height: 120px;
    }

    .text-center {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
    }

    .custom-scroll::-webkit-scrollbar {
        width: 5px;
    }

    .custom-scroll::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    .custom-scroll::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
    }

    .custom-scroll::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    .start-chat-button {
        display: flex;
        justify-content: center;
        padding: 80px 0;
    }

    .start-chat-button>button {
        background-color: #28a745 !important;
        color: #fff !important;
        border-color: #28a745 !important;
        min-width: 150px !important;
    }

    .rooms-search {
        display: flex;
        align-items: center;
        padding: 25px 10px 10px;
    }

    .rooms-version-label {
        position: absolute;
        left: 5px;
        top: 5px;
        color: #888;
        font-size: 10px;
    }

    .rooms-search-input {
        width: 100%;
    }

    .rooms-search-input input {
        border: 1px solid #ddd;
        width: calc(100% - 20px);
        height: 40px;
        padding: 0 12px;
        border-radius: 4px;
        color: #000;
        outline: none !important;
    }

    .rooms-search-button {
        width: 50px;
        height: 40px;
    }

    .rooms-search-button-search-image {
        height: 22px;
    }

    .rooms-search-button-image {
        height: 20px;
    }

    .rooms-search-button-image-small {
        height: 16px;
    }

    .rooms-search-button-filter-image {
        height: 24px;
    }

    .rooms-search-button-send-image {
        height: 19px;
    }

    .vac-room-name {
        display: flex;
        align-items: center;
        line-height: 14px;
        font-size: 13px;
    }

    .rooms-search-button-info {
        height: 19px;
        margin-right: 5px;
        cursor: pointer;
    }

    .rooms-search-button-info:hover {
        opacity: 0.6;
    }

    .rooms-search-button-send-disabled {
        pointer-events: none;
        opacity: 0.4;
    }

    .rooms-search-button-element {
        background-color: transparent !important;
        outline: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
        min-width: 100%;
        width: 100%;
        max-width: 100%;
        min-height: 100%;
        height: 100%;
        max-height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .rooms-search-button-element:before,
    .rooms-search-button-element:focus:before,
    .rooms-search-button-element:hover:before {
        background-color: transparent !important;
    }

    .rooms-search-button-element:hover {
        opacity: 0.6;
    }

    .room-options-actions {
        display: flex;
        align-items: center;
        width: max-content;
        padding-left: 5px;
    }

    .rooms-button-element {
        background-color: transparent !important;
        outline: none !important;
        box-shadow: none !important;
        padding: 0 5px !important;
        margin: 0 !important;
        min-width: auto;
        min-height: 100%;
        height: 100%;
        max-height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .rooms-button-element:before,
    .rooms-button-element:focus:before,
    .rooms-button-element:hover:before {
        background-color: transparent !important;
    }

    .rooms-button-element:not(.rooms-button-icon):hover {
        opacity: 0.6;
    }

    .rooms-button-icon {
        padding-left: 0 !important;
    }

    .rooms-button-element-no-space {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .rooms-button-element-image {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        max-width: 40px;
        white-space: initial;
    }

    .rooms-button-element-image>b {
        line-height: 9px;
        font-size: 10px;
        text-transform: capitalize;
    }

    .rooms-pagination {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding-bottom: 10px;
    }

    .rooms-pagination-flex {
        padding: 0 20px;
        justify-content: space-between;
    }

    .rooms-pagination-page {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .rooms-pagination-page-arrow {
        display: flex;
        align-items: center;
        height: 25px;
    }

    .rooms-pagination-page-arrow>div {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 25px;
        height: 100%;
        cursor: pointer;
        background-color: #0696c7;
        border: 1px solid #0696c7;
    }

    .rooms-pagination-page-arrow.left>div {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    }

    .rooms-pagination-page-arrow.right>div {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }

    .rooms-pagination-page-arrow>div:hover {
        opacity: 0.8;
    }

    .rooms-pagination-page-value {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        width: 60px;
        height: 25px;
        border-top: 1px solid #0696c7;
        border-bottom: 1px solid #0696c7;
        font-size: 16px;
    }

    .rooms-pagination-page-value input {
        width: 100%;
        height: 100%;
        text-align: center;
        border: none;
        box-shadow: none;
        outline: none;
    }

    .rooms-pagination-page img {
        height: 16px;
    }

    .rooms-pagination-employee {
        font-size: 12px;
        font-style: italic;
    }

    .rooms-info-label {
        font-size: 10px;
        line-height: 12px;
    }

    .pt-clients {
        position: absolute;
        left: 35px;
        top: 50px;
        width: 280px;
        max-height: 400px;
        background-color: white;
    }

    .pt-clients-dark {
        border: 1px solid #000;
        box-shadow: 5px 5px 10px #000;
    }

    .pt-clients-light {
        border: 1px solid #ddd;
        box-shadow: 5px 5px 10px #ececec;
    }

    .pt-clients-search {
        padding: 10px;
    }

    .pt-clients-search input {
        border: 1px solid #ddd;
        border-radius: 0;
        width: 100%;
        height: 40px;
        padding: 10px;
        color: #000;
        outline: none !important;
    }

    .pt-clients-list {
        max-height: 330px;
        overflow: auto;
        padding: 5px 0;
    }

    .pt-clients-list>div {
        padding: 5px 10px;
        cursor: pointer;
    }

    .pt-clients-list>div:hover {
        opacity: 0.6;
    }

    .pt-clients-list::-webkit-scrollbar {
        width: 10px;
    }

    .pt-clients-list::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    .pt-clients-list::-webkit-scrollbar-thumb {
        background: #888;
    }

    .pt-clients-list::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    .message-content-file {
        display: flex;
        align-items: center;
        padding: 0 10px;
    }

    .message-content-file-delete {
        margin-left: 10px;
        height: 20px;
        color: #dc3545;
        cursor: pointer;
    }

    .message-content-file-delete:hover {
        opacity: 0.6;
    }

    .load-more-messages-button>button {
        background-color: #888 !important;
        color: #fff !important;
        border-color: #888 !important;
    }

    .select-room-options.menuable__content__active {
        top: 52px !important;
        z-index: 99 !important;
    }

    .select-room-options .v-list>div {
        padding: 8px 12px;
        cursor: pointer;
        text-align: center;
        border-bottom: 1px solid #efefef;
    }

    .select-room-options .v-list {
        padding: 0;
    }

    .select-room-options .v-list>div:last-child {
        border-bottom: none;
    }

    .select-room-options .v-list>div:hover {
        opacity: 0.6;
    }

    .select-room-options-btn {
        padding: 0;
        margin: 0;
        min-width: auto;
        box-shadow: none !important;
        outline: none !important;
        background-color: #efefef !important;
        border-radius: 100%;
        width: 32px;
        height: 32px;
    }

    .select-room-options-btn:hover {
        opacity: 0.6;
    }

    .select-room-options-btn::before,
    .select-room-options-btn::after {
        background-color: transparent !important;
    }

    .select-room-options-btn .v-ripple__container {
        display: none !important;
    }

    .select-room-options-btn img {
        height: 22px;
    }

    .dialog-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #00000059;
    }

    .dialog-container-popover {
        padding-top: 20px;
        height: auto;
        z-index: 999;
        align-items: flex-start;
        background-color: transparent;
    }

    .dialog-container-popover .dialog-container-body {
        max-height: 300px;
        overflow: auto;
    }

    .dialog-container-box {
        border: 1px solid #ddd;
        width: 80%;
        max-width: 600px;
        background-color: #fff;
        border-radius: 6px;
    }

    .dialog-container-header {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 25px;
        padding: 10px 10px 0;
    }

    .dialog-container-body {
        padding: 10px 20px;
    }

    .dialog-container-body textarea {
        border: 1px solid #ddd;
        min-height: 120px;
        padding: 10px;
    }

    .dialog-container-body .v-input {
        border-color: transparent !important;
        color: transparent !important;
    }

    .dialog-container-body .v-input__slot:before {
        display: none !important;
    }

    .dialog-container-body .v-input__slot:after {
        display: none !important;
    }

    .dialog-container-body-filter-days {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .dialog-container-body-filter-days input {
        border: 1px solid #ddd;
        border-radius: 0;
        width: 100%;
        height: 40px;
        max-height: 40px;
        padding: 10px;
        color: #000;
        outline: none !important;
        width: 80px;
        max-width: 80px;
    }

    .dialog-container-body-filter-days>div:first-child {
        margin-right: 10px;
    }

    .dialog-container-body-filter-days>div:last-child {
        margin-left: 10px;
    }

    .dialog-container-footer {
        padding: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .dialog-container-button-confirm {
        margin-right: 20px;
        background-color: #28a745 !important;
        color: #fff !important;
    }

    .dialog-container-button-confirm:hover {
        opacity: 0.6;
    }

    .dialog-container-button-cancel {
        background-color: #888 !important;
        color: #fff !important;
    }

    .dialog-container-button-confirm:hover {
        opacity: 0.6;
    }

    .message-load-media-icon {
        cursor: pointer;
        border: 1px solid #509dc8;
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 30px;
        height: 30px;
        background-color: #e5effa;
        padding: 10px;
    }

    .message-load-media-icon:hover {
        opacity: 0.8;
    }

    .message-load-media-icon>img {
        height: 100%;
    }

    .dialog-room-notes-title {
        padding: 5px 0 0;
    }

    .dialog-room-notes-title-break {
        white-space: break-spaces;
    }

    .dialog-room-notes-no-data {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 30px 20px;
        color: #888;
    }

    .rooms-filter-tabs {
        display: flex;
        align-items: center;
        padding-top: 5px;
    }

    .rooms-filter-tabs-item {
        width: 100%;
        border-bottom: 1px solid #ddd;
        text-align: center;
        padding: 2px 8px;
        cursor: pointer;
        font-size: 7px;
        height: 25.5px;
    }

    .rooms-filter-tabs-item:hover {
        opacity: 0.7 !important;
    }

    .rooms-filter-tabs-item-text {
        opacity: 0.5;
    }

    .rooms-filter-tabs-item-value {
        font-family: boldFont;
    }

    .rooms-filter-tabs-item.active {
        border-color: #0696c7;
        font-family: boldFont;
    }

    .rooms-filter-tabs-item.active .rooms-filter-tabs-item-text {
        opacity: 1;
    }

    .list-fixed-messages-items {
        padding: 10px;
        height: 130px;
        overflow: auto;
        position: absolute;
        left: 0;
        top: 50px;
        width: calc(100% - 20px);
        background-color: #efefef;
        box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
    }

    .list-fixed-messages-items-list {
        zoom: 0.8;
    }

    .list-fixed-messages-item {
        border-radius: 6px;
        background-color: #f8f9fa;
        box-shadow: 0 1px 1px -1px #0000001a, 0 1px 1px -1px #0000001c, 0 1px 2px -1px #0000001c;
        width: fit-content;
        padding: 6px 10px;
        margin-bottom: 10px;
    }

    .list-fixed-messages-item button {
        border-radius: 20px;
        height: 30px;
        font-size: 12px;
    }

    .list-fixed-messages-item-pt {
        background-color: #ccf2cf;
    }
    
    .list-fixed-messages-item-title {
        font-family: boldFont;
    }

    .list-fixed-messages-item-icon {
        height: 18px;
        margin-left: 3px;
        cursor: pointer;
    }

    .list-fixed-messages-item-icon:hover {
        opacity: 0.6;
    }

    .dialog-fixed-message>div {
        width: 100%;
    }

    .dialog-fixed-message img {
        max-width: 100%;
    }

    .dialog-fixed-message video {
        max-width: 100%;
    }

    .dialog-images {
        margin: 0 0 10px;
        padding: 0;
        max-height: 75vh;
        overflow: auto;
    }

    .dialog-images-item {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding-bottom: 10px;
        border: 1px solid #dedede;
        border-bottom: none;
    }

    .dialog-images-item:last-child {
        padding-bottom: 0;
        border-bottom: 1px solid #dedede;
    }

    .dialog-images-item>div:first-child{
        width: 100%;
        padding: 3px 0;
        background-color: #f5f5f5;
        color: #888;
        text-align: center;
    }

    .dialog-images-item img {
        max-width: 100%;
    }

    .dialog-images-button {
        display: flex;
        justify-content: center;
        padding-top: 10px;
    }

    .dialog-images-button>button {
        box-shadow: none !important;
        border: 1px solid #dedede;
        border-radius: 12px;
        width: 170px;
        height: 30px;
        font-size: 12px;
        padding: 0;
    }

    .text-ellipsis {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis; 
    }

    .cursor-hover {
        cursor: pointer;
    }

    .cursor-hover:hover {
        opacity: 0.7;
    }

    .rooms-box-float {
        z-index: 9999 !important;
    }

    .rooms-box-float-content {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .rooms-box-float-content-scroll {
        height: 100%;
        overflow: auto;
        padding: 0 15px 15px;
        margin-bottom: 5px;
    }

    .rooms-box-float-content-title {
        font-weight: bold;
        padding-top: 10px;
    }
</style>

<script>
    import Api from '../services/Api'
    import Utils from '../services/Utils'
    import Keyboard from '@/components/Keyboard'
    import VueDragResize from 'vue-drag-resize'
    import { register } from 'vue-advanced-chat'
    register()

    let newMessagesPromise = null
    let roomNamesPromise = null
    let newRoomMessagesPromise = null
    let autoRefreshMessagesCountPromise = null
    let refreshDraftsPromise = null

    export default {
        components: {
            Keyboard,
            VueDragResize,
        },
        data() {
            const isIos = navigator.userAgent.match(/iPhone|iPad|iPod/i) ? true : false
            return {
                appVersion: window.appVersion,
                str: null,
                datepickerLanguage: window.datepickerLanguage,
                apiFailed: false,
                maintenanceMode: false,
                ptConfig: Utils.getPtConfig(),
                autoRefreshPromisesEnabled: true,
                notificationsEnabled: true,
                newMessagesPromiseTime: 120000,
                newRoomMessagesPromiseTime: 10000,
                changeRoomNameEnabled: false,
                roomNamesPromiseTime: 300000,
                draftEnabled: false,
                refreshDraftsPromiseTime: 120000,
                refreshChatPromiseTime: 5000,
                roomOptionsEnabled: false,
                roomOptions: [],
                roomFixedEnabled: false,
                roomTypingEnabled: false,
                roomArchivedEnabled: false,
                roomsPaginationEnabled: false,
                pagesEmployeesEnabled: false,
                pagesEmployees: {},
                employeePageToSend: false,
                initialTotalRooms: 20,
                getMessagesLimitParameter: 20,
                loaded: false,
                chatVisible: true,
                imageMaxSize: 1200,
                chatUser: null,
                theme: window.theme,
                roomsLoaded: false,
                messagesLoaded: false,
                messageActions: [],
                loadingRooms: false,
                cacheFilteredRooms: [],
                filteredRooms: [],
                vueAdvancedChatRooms: [],
                roomsSearchValue: '',
                textMessages: {},
                showAudio: window.audioEnabled ? 'true' : 'false',
                singleRoom: !window.isPT ? true : false,
                loadFirstRoom: !window.isPT ? 'true' : 'false',
                acceptedFiles: window.isPT ? 'image/png, image/jpeg, image/heic, audio/mp3' : 'image/png, image/jpeg, image/heic',
                virtualKeyboardEnabled: false,
                virtualKeyboardVisible: false,
                nativeKeyboardEnabled: false,
                roomDefaultMessage: '',
                openDashboardClientEnabled: false,
                clientsListSearch: '',
                rooms: [],
                messages: [],
                vueAdvancedChatMessages: [],
                requestsProcessing: {},
                newClientKey: '[novo]',
                filesTypes: {
                    text: 0,
                    image: 1,
                    audio: 2,
                    video: 3,
                },
                messageStatus: {
                    created: 0,
                    delivered: 1,
                    read: 2,
                },
                ptUserChatId: null,
                sendMessageToAllVisible: false,
                messageToAllValue: '',
                sendMessageToAllEnabled: false,
                filtersEnabled: false,
                filtersGroupsEnabled: false,
                filtersVisible: false,
                filterKeys: {
                    newRegisters: 'new_registers',
                    clientsWithNewMessages: 'client_with_new_messages',
                    clientsWithDraft: 'client_with_draft',
                    unansweredClients: 'unanswered_clients',
                    archivedConversations: 'archived_conversations',
                    moreThanUnreadMessages: 'filter_more_than_unread_messages'
                },
                filters: [],
                filtersActive: [],
                currentRoomsPage: 0,
                currentRoomsPageInput: 1,
                searchPromise: null,
                showNotes: false,
                roomUser: null,
                foods: {},
                roomNotesVisible: false,
                isIos: isIos,
                autoLoadMessagesFiles: false,
                autoLoadMessagesFilesLimit: 5,
                tagsEnabled: false,
                tagsDict: {},
                filterEmployeeEnabled: false,
                pendingFeedbacksEnabled: false,
                updateFeedbackDateEnabled: false,
                pendingFeedbacks: [],
                dialogPendingFeedback: false,
                pendingFeedbackClient: null,
                pendingFeedbackMenu: null,
                roomFlagsEnabled: false,
                sendVideoEnabled: false,
                messagesVideosCache: {},
                roomsNamesFeatures: {},
                openDashboardRegisterEnabled: false,
                IASummaryEnabled: false,
                dialogIASummary: false,
                dialogIASummaryContent: null,
                roomUpdateTagsEnabled: false,
                newCalendarEventEnabled: false,
                dialogUpdateTags: false,
                roomUpdateTagsData: null,
                replyMessageEnabled: false,
                fixMessageEnabled: false,
                fixedMessages: null,
                dialogFixedMessage: false,
                dialogFixedMessageData: null,
                increaseFontSizeEnabled: false,
                loadingWaitActive: false,
                dialogNewCalendarEvent: false,
                newCalendarEventData: [{
                    type: 'datepicker',
                    id: 'start_date',
                    title: 'start_date',
                    value: '',
                    required: true,
                    menu: false
                }, {
                    type: 'datepicker',
                    id: 'end_date',
                    title: 'end_date',
                    value: '',
                    required: true,
                    menu: false
                }, {
                    type: 'textarea',
                    id: 'description',
                    title: 'description',
                    value: '',
                    required: true
                }],
                filesFolder: '',
                showImagesEnabled: false,
                dialogImages: false,
                dialogImagesData: null,
                roomBox: {
                    width: 0,
                    height: 0,
                    top: 0,
                    left: 0,
                },
                saveFeedbackMessageEnabled: false,
                showSubscriptionEnabled: false,
                showHasPlansEnabled: false,
                deleteRoomEnabled: false,
                roomSummaryNotesEnabled: false,
                dialogRoomSummaryNotes: null,
                useRoomAdminUnread: false,
                filterCategoriesEnabled: false,
                hideRoomUnreadMessagesValueEnabled: false,
                chatCategories: [],
                categoryFilterActive: '',
                focusRefreshChatEnabled: false,
                autoRefreshMessagesCountEnabled: false,
                autoRefreshMessagesCountPromiseTime: 600000,
                dialogRoomLastFeedbackDate: '',
            }
        },
        computed: {
            groupFilterActive() {
                const active = this.filters.find(function (item) {
                    return item.group && item.value
                })
                if (active) {
                    return active.id
                } else {
                    return 'no_tag'
                }
            },
            archivedConversationsFilterActive() {
                const self = this
                if (this.roomArchivedEnabled) {
                    const filterActive = this.filtersActive.find(function (f) {
                        return f.id === self.filterKeys.archivedConversations
                    })
                    if (filterActive) {
                        return true
                    }
                }
                return false
            },
        },
        beforeMount: function () {
            this.init()
        },
        methods: {
            init: function () {
                this.handlePtConfig()
                this.getStringsAndEnums()
                this.onRefreshChatEvent()
            },
            onRefreshChatEvent: function () {
                const self = this
                document.body.addEventListener('socket-refresh-chat', function () {
                    self.refreshChat(true)
                })
            },
            handlePtConfig: function () {
                const self = this
                const ptId = window.chatUserPtId ? window.chatUserPtId : window.ptUserId
                const json = this.ptConfig[ptId]
                if (json) {
                    for (const key in json) {
                        if (key === 'filesFolder') {
                            self.filesFolder = json[key]
                        } else {
                            if (json[key].all ||
                                (((json[key].dev && window.apiDev) || !json[key].dev) &&
                                ((json[key].onlyPt && window.isPT) || !json[key].onlyPt) &&
                                ((json[key].onlyClient && !window.isPT) || !json[key].onlyClient) &&
                                ((json[key].onlyIos && this.isIos) || !json[key].onlyIos) &&
                                (json[key].onlyPt || json[key].onlyClient))
                            ) {
                                self[json[key].field] = json[key].value ? json[key].value : true
                                if (json[key].addValue) {
                                    self[json[key].addValue.field] += json[key].addValue.value
                                }
                                if (json[key].replaceValue) {
                                    self[json[key].replaceValue.field] = json[key].replaceValue.value
                                }
                                if (json[key].onlyRoomsNames) {
                                    self.roomsNamesFeatures[json[key].field] = json[key].onlyRoomsNames
                                }
                            }
                        }
                    }
                }
            },
            getStringsAndEnums: function () {
                const self = this
                this.apiFailed = false
                Api.pt.getStrings(function (response) {
                    if (response.success) {
                        window.strings = response.data
                        self.str = response.data
                        Api.pt.getEnums(function () {
                            self.main()
                        })
                    } else {
                        self.apiFailed = true
                        self.loaded = true
                    }
                })
            },
            getFoods: function (callback) {
                const self = this
                if (this.showNotes) {
                    if (Object.keys(this.foods).length) {
                        callback()
                    } else {
                        this.$isLoading(true)
                        Api.pt.getFoods(function (response) {
                            self.$isLoading(false)
                            if (response.success) {
                                const foods = {}
                                response.data.forEach(function (item) {
                                    foods[item.id] = item.name
                                })
                                self.foods = foods
                                callback()
                            } else {
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }
                }
            },
            main: function () {
                if (!this.checkChatStatus()) {
                    return false
                }

                this.setUserTagsDict()
                this.setChatCategories()
                this.refreshChatStrings()
                this.roomsLoaded = false
                this.$isLoading(true)
                this.getChatUser()

                if (this.autoRefreshPromisesEnabled && !window.socketsEnabled) {
                    this.activeAutoRefreshPromises()
                }

                if (this.notificationsEnabled) {
                    this.activeAutoRefreshNotifications()
                }

                if (this.autoRefreshMessagesCountEnabled) {
                    this.activeAutoRefreshMessagesCount()
                }

                if (window.isPT) {
                    this.receiveDashboardEvents()
                    this.setDefaultFilters()
                    this.onKeydown()
                }

                if (this.draftEnabled && this.roomTypingEnabled) {
                    this.activeAutoRefreshDraftsPromise()
                }

                if (this.pagesEmployeesEnabled) {
                    this.getPagesEmployees()
                }
            },
            checkChatStatus: function () {
                try {
                    if (window.enums && window.enums.chat_maintenance_mode) {
                        this.$isLoading(false)
                        this.maintenanceMode = true
                        this.loaded = true
                        this.sendEventToDashboard({
                            event: 'new-messages',
                            data: 0
                        })
                        return false
                    }
                } catch { }
                return true
            },
            setUserTagsDict: function () {
                const statusList = Utils.getUserTags()
                const dict = {}
                statusList.forEach(function (item) {
                    dict[item.value] = item
                })
                this.tagsDict = dict
            },
            setChatCategories: function () {
                this.chatCategories = Utils.getChatCategories()
            },
            refreshChatStrings: function () {
                this.textMessages = {
                    ROOMS_EMPTY: this.str['rooms_empty'],
                    ROOM_EMPTY: this.str['room_empty'],
                    NEW_MESSAGES: this.str['new_messages'],
                    MESSAGE_DELETED: this.str['message_deleted'],
                    MESSAGES_EMPTY: this.str['messages_empty'],
                    CONVERSATION_STARTED: this.str['conversation_started'],
                    TYPE_MESSAGE: this.str['type_message'],
                    SEARCH: this.str['search'],
                    IS_ONLINE: this.str['is_online'],
                    LAST_SEEN: this.str['last_seen'],
                    IS_TYPING: this.str['is_typing'],
                    CANCEL_SELECT_MESSAGE: this.str['cancel_select_message'],
                }
                const messageActions = []
                if (this.replyMessageEnabled) {
                    messageActions.push({
                        name: 'replyMessage',
                        title: this.str['reply']
                    })
                }
                if (this.fixMessageEnabled) {
                    messageActions.push({
                        name: 'fixMessage',
                        titles: {
                            0: window.strings['fix'],
                            1: window.strings['unfix']
                        },
                        field: 'pinned'
                    })
                }
                if (this.saveFeedbackMessageEnabled) {
                    messageActions.push({
                        name: 'saveFeedbackMessage',
                        title: this.str['save_in_last_feedback']
                    })
                }
                messageActions.push({
                    name: 'editMessage',
                    title: this.str['edit_message'],
                    onlyMe: true
                }, {
                    name: 'deleteMessage',
                    title: this.str['delete_message'],
                    onlyMe: true
                }, {
                    name: 'markAsUnread',
                    title: this.str['mark_as_unread'],
                    onlyMe: false
                })
                this.messageActions = messageActions
            },

            // Main requests
            getChatUser: function () {
                const self = this

                if (window.chatUserId) {
                    self.chatUser = {
                        chat_id: window.chatUserId,
                        pt_id: window.chatUserPtId,
                        id: self.newClientKey,
                        name: ''
                    }
                    self.refreshSocketSubscription()
                    if (self.chatUser.pt_id) {
                        afterGetUser()
                    }
                    return true
                }

                if (window.registerUserId) {
                    self.handleChatUserWithRegisterId(
                        window.registerUserId,
                        function (details) {
                            self.chatUser = {
                                pt_id: window.chatUserPtId,
                                name: details.name,
                                email: details.email,
                                password: details.password,
                                chat_id: details.chat_id,
                                chat_token: window.chatUserToken ? window.chatUserToken : details.chat_token
                            }
                            self.refreshSocketSubscription()
                            if (self.chatUser.pt_id) {
                                afterGetUser(window.registerUserId)
                            }
                        }
                    )
                    return true
                }

                Api.pt.getUser(window.ptUserId, function (response) {
                    if (response.success) {
                        self.chatUser = response.data[0]
                        if (!window.chatUserPtId) {
                            window.chatUserPtId = self.chatUser.pt_id
                        }
                        afterGetUser()
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })

                function afterGetUser(registerId) {
                    self.handleRegisterUser({
                        user: self.chatUser,
                        registerId: registerId,
                        callback: function (registerUser) {
                            const registerUserId = registerUser.id
                            if (registerUserId) {
                                self.chatUser.chat_id = registerUserId
                                self.refreshSocketSubscription()
                                self.getRooms(function () {
                                    self.validateTriggerChatUserId()
                                    if (!self.rooms.length && !window.isPT) {
                                        clientChatCreateRoom(registerId)
                                    }
                                })
                            } else {
                                self.rooms = []
                                self.applySearchRoomsValue()
                                self.roomsLoaded = true
                                self.$isLoading(false)
                                self.loaded = true
                            }
                        }
                    })
                }

                function clientChatCreateRoom(registerId) {
                    if (self.ptUserChatId) {
                        self.openChat({
                            chat_id: self.chatUser.chat_id,
                            users: [self.ptUserChatId, self.chatUser.chat_id],
                            registerId: registerId
                        })
                    } else {
                        Api.pt.getUser(self.chatUser.pt_id, function (response) {
                            if (response.success) {
                                self.ptUserChatId = response.data[0].chat_id
                                self.openChat({
                                    chat_id: self.chatUser.chat_id,
                                    users: [self.ptUserChatId, self.chatUser.chat_id],
                                    registerId: registerId
                                })
                            }
                        })
                    }
                }
            },
            refreshSocketSubscription: function () {
                if (window.socketsEnabled) {
                    const substring = (window.mqttKeys[window.apiKey] ? (window.mqttKeys[window.apiKey] + '/') : '') + (window.apiDev ? 'dev/' : '')
                    window.mqttClient.subscribe(substring + 'chat/' + this.chatUser.chat_id)
                    if (this.pagesEmployeesEnabled) {
                        window.mqttClient.subscribe(substring + 'chat/page')
                    }
                    if (this.pendingFeedbacksEnabled) {
                        window.mqttClient.subscribe(substring + 'chat/' + window.chatUserPtId + '/feedbacks')
                    }
                    if (this.roomFlagsEnabled) {
                        window.mqttClient.subscribe(substring + 'chat/flags')
                    }
                }
            },
            validateTriggerChatUserId: function () {
                const self = this
                if (window.triggerChatUserId) {
                    const userChatId = parseInt(window.triggerChatUserId)
                    this.handleRegisterUser({
                        user: {
                            chat_id: userChatId
                        },
                        callback: function (registerUser) {
                            self.openChat({
                                chat_id: userChatId,
                                users: [self.chatUser.chat_id, registerUser.id]
                            })
                        }
                    })
                }
            },
            handleRegisterUser: async function (args) {
                const self = this
                const user = args.user
                const registerId = args.registerId
                const callback = args.callback

                if (user.chat_id) {
                    const chatUser = await getRegisterUser()

                    if (chatUser === 'error') {
                        return false
                    }

                    if (chatUser) {
                        callback(chatUser)
                    } else {
                        args.user.chat_id = null
                        self.handleRegisterUser(args)
                    }

                } else {
                    if (!user.password && registerId) {
                        return self.handleChatUserWithRegisterId(
                            registerId,
                            function (details) {
                                args.user.pt_id = window.ptUserId
                                args.user.name = details.name
                                args.user.email = details.email
                                args.user.password = details.password
                                args.user.chat_token = details.chat_token
                                self.handleRegisterUser(args)
                            }
                        )
                    }

                    const password = await decryptPassword()
                    if (password === 'error') {
                        return false
                    }

                    const newUserId = await newRegisterUser(password)
                    if (newUserId === 'error') {
                        return false
                    }

                    let chatId = null
                    if (registerId) {
                        chatId = await updateRegister(newUserId)
                        if (chatId === 'error') {
                            return false
                        }
                    } else {
                        chatId = await updateUser(newUserId)
                        if (chatId === 'error') {
                            return false
                        }
                    }

                    callback({
                        id: chatId
                    })
                }

                function getRegisterUser() {
                    return new Promise(resolve => {
                        Api.chat.getUser(user.chat_id, function (response) {
                            if (response.success) {
                                if (response.data[0]) {
                                    resolve(response.data[0])
                                } else {
                                    resolve(null)
                                }
                            } else {
                                self.$isLoading(false)
                                resolve('error')
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    })
                }

                function decryptPassword() {
                    return new Promise(resolve => {
                        Api.pt.decryptPassword(user.password, function (response) {
                            if (response.success) {
                                if (response.data.password) {
                                    resolve(response.data.password)
                                } else {
                                    resolve('test')
                                }
                            } else {
                                self.$isLoading(false)
                                resolve('error')
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    })
                }

                function newRegisterUser(password) {
                    return new Promise(resolve => {
                        Api.chat.getUserByEmail(user.email, function (response) {
                            if (response.success) {
                                if (response.data[0]) {
                                    resolve(response.data[0].id)
                                } else {
                                    newUser()
                                }
                            } else {
                                newUser()
                            }
                        })

                        function newUser() {
                            const payload = {
                                token: user.chat_token ? user.chat_token : (self.chatUser.chat_token ? self.chatUser.chat_token : ''),
                                name: user.name,
                                email: user.email,
                                password: password
                            }
                            Api.chat.newUser(payload, function (response) {
                                if (response.success) {
                                    resolve(response.data.id)
                                } else {
                                    self.$isLoading(false)
                                    resolve('error')
                                    self.$alert(
                                        response.message,
                                        '',
                                        'warning',
                                        Utils.getAlertOptions()
                                    )
                                }
                            })
                        }
                    })
                }

                function updateUser(chatId) {
                    return new Promise(resolve => {
                        const payload = {
                            id: user.id,
                            pt_id: user.pt_id,
                            chat_id: chatId
                        }
                        Api.pt.updateUser(payload, function (response) {
                            if (response.success) {
                                resolve(response.data.chat_id)
                            } else {
                                self.$isLoading(false)
                                resolve('error')
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    })
                }

                function updateRegister(chatId) {
                    return new Promise(resolve => {
                        const payload = {
                            id: registerId,
                            chat_id: chatId
                        }
                        Api.pt.updateClientRegister(payload, function (response) {
                            if (response.success) {
                                resolve(response.data.chat_id)
                            } else {
                                self.$isLoading(false)
                                resolve('error')
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    })
                }
            },
            getRooms: async function (callback) {
                const self = this

                if (this.requestsProcessing.getMessages) {
                    return false
                }

                this.requestsProcessing.getMessages = true

                const fields = await getChatsFields()
                if (fields.chatsUnreadCount === 'error') {
                    return finish(false)
                }

                let chats = await getChats()
                if (chats === 'error') {
                    return finish(false)
                }

                const defaultDate = new Date('1994-01-19')
                chats = chats.sort(function (a, b) {
                    const lastMessageDateA = a.lastMessageDate ? new Date(a.lastMessageDate) : defaultDate
                    const lastMessageDateB = b.lastMessageDate ? new Date(b.lastMessageDate) : defaultDate
                    return lastMessageDateB - lastMessageDateA
                })

                finish(chats)

                function getChatsFields() {
                    return new Promise(resolve => {
                        if (self.singleRoom) {
                            return resolve({
                                chatsUnreadCount: {},
                                chatLastMessageDates: {} 
                            })
                        }
                        Api.chat.getNewMessagesCount(self.chatUser.chat_id, function (response) {
                            if (response.success) {
                                const chatsUnreadCount = {}
                                const chatLastMessageDates = {}
                                if (response.data && response.data.details) {
                                    for (let i = 0; i < response.data.details.length; i++) {
                                        const detail = response.data.details[i]
                                        if (detail.unread_count) {
                                            chatsUnreadCount[detail.chat_id] = detail.unread_count
                                        }
                                        chatLastMessageDates[detail.chat_id] = detail.last_msg_dt
                                    }
                                }
                                resolve({
                                    chatsUnreadCount: chatsUnreadCount,
                                    chatLastMessageDates: chatLastMessageDates
                                })
                            } else {
                                self.$isLoading(false)
                                resolve('error')
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    })
                }

                function getChats() {
                    return new Promise(resolve => {
                        if (self.singleRoom && !self.ptUserChatId) {
                            Api.pt.getUser(self.chatUser.pt_id, function (response) {
                                if (response.success) {
                                    self.ptUserChatId = response.data[0].chat_id
                                    getData()
                                } else {
                                    self.$isLoading(false)
                                    resolve('error')
                                    self.$alert(
                                        response.message,
                                        '',
                                        'warning',
                                        Utils.getAlertOptions()
                                    )
                                }
                            })
                        } else {
                            getData()
                        }

                        function getData() {
                            const payload = {
                                pt_chat_id: !window.isPT ? self.ptUserChatId : null,
                                id: self.chatUser.chat_id,
                                expanded: self.singleRoom,
                                months: window.isPT ? 1 : null
                            }
                            Api.chat.getChats(payload, function (response) {
                                if (response.success) {
                                    for (let i = 0; i < response.data.length; i++) {
                                        if (fields.chatsUnreadCount[response.data[i].id]) {
                                            response.data[i].unread_count = fields.chatsUnreadCount[response.data[i].id]
                                        } else {
                                            response.data[i].unread_count = 0
                                        }
                                        if (fields.chatLastMessageDates[response.data[i].id]) {
                                            response.data[i].last_msg_utc = fields.chatLastMessageDates[response.data[i].id]
                                        }
                                    }
                                    resolve(self.encodeRooms(response.data))
                                } else {
                                    self.$isLoading(false)
                                    resolve('error')
                                    self.$alert(
                                        response.message,
                                        '',
                                        'warning',
                                        Utils.getAlertOptions()
                                    )
                                }
                            })
                        }
                    })
                }

                function finish(rooms) {
                    if (rooms) {
                        self.rooms = rooms
                    } else {
                        self.rooms = []
                    }

                    self.applySearchRoomsValue()
                    self.roomsLoaded = true
                    self.$isLoading(false)
                    self.loaded = true
                    self.sendEventToApp('loaded')
                    delete self.requestsProcessing.getMessages

                    if (callback) {
                        callback()
                    }
                }
            },
            sendEventToApp: function (event) {
                try {
                    if (this.singleRoom && window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(JSON.stringify({
                            event: event
                        }))
                    }
                } catch { }
            },
            openChat: function (args) {
                const self = this

                if (!getCacheChat()) {
                    getBackendChat(function (result) {
                        if (!result) {
                            newChat()
                        }
                    })
                }

                function getCacheChat() {
                    const room = self.rooms.find(function (item) {
                        if (window.isPT) {
                            return self.chatUser.chat_id !== item.users[0]._id && args.users.indexOf(item.users[0]._id) > -1
                        } else {
                            return args.users.indexOf(item.users[0]._id) > -1
                        }
                    })

                    if (room) {
                        self.openRoom(room.roomId)
                        return room
                    }

                    return false;
                }

                function getBackendChat(callback) {
                    Api.chat.getChats({
                        pt_chat_id: window.isPT ? self.chatUser.chat_id : self.ptUserChatId,
                        id: args.chat_id
                    }, function (response) {
                        if (response.success) {
                            if (response.data.length) {
                                checkEmployees(response.data[0], function () {
                                    for (let i = 0; i < response.data.length; i++) {
                                        response.data[i].unread_count = 0
                                    }

                                    let rooms = JSON.parse(JSON.stringify(self.rooms))
                                    rooms = rooms.concat(self.encodeRooms(response.data))
                                    self.rooms = rooms
                                    self.applySearchRoomsValue()

                                    setTimeout(function () {
                                        self.openRoom(response.data[0].id)
                                        callback(true)
                                    })
                                })
                            } else {
                                callback(false)
                            }
                        } else {
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })

                    function checkEmployees(roomItem, cb) {
                        if (self.filterEmployeeEnabled && window.logId && window.logId !== window.chatUserPtId) {
                            let notes = {}
                            try {
                                if (roomItem.notes) {
                                    notes = JSON.parse(roomItem.notes)
                                }
                            } catch { }
                            if (!notes) {
                                notes = {}
                            }
                            if (!notes.employees) {
                                notes.employees = []
                            }
                            if (notes.employees.indexOf(window.logId) === -1) {
                                notes.employees.push(window.logId)
                                return self.updateChatRoom({
                                    id: roomItem.id,
                                    notes: JSON.stringify(notes),
                                    forceRefresh: true,
                                    callback: function () {
                                        getBackendChat(callback)
                                    }
                                })
                            }
                        }
                        cb()
                    }
                }

                function newChat() {
                    if (args.registerId) {
                        Api.pt.getClientRegister(args.registerId, function (response) {
                            if (response.success) {
                                const client = response.data[0]
                                createNewChat(self.newClientKey + ' - ' + client.name)
                            } else {
                                self.$isLoading(false)
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    } else {
                        Api.pt.getUserByChatId({
                            chatId: args.chat_id,
                            ptId: self.chatUser.pt_id
                        }, function (response) {
                            if (response.success && response.data && response.data[0]) {
                                const client = response.data[0]
                                createNewChat(client.id + ' - ' + client.name)
                            } else {
                                self.$isLoading(false)
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }

                    function createNewChat(name) {
                        const newChatPayload = {
                            name: name,
                            type: 0,
                            users: args.users,
                        }
                        if (self.filterEmployeeEnabled && window.logId && window.logId !== window.chatUserPtId) {
                            newChatPayload.notes = JSON.stringify({
                                employees: [window.logId]
                            })
                        }
                        Api.chat.newChat(newChatPayload, function (response) {
                            if (response.success) {
                                const rooms = JSON.parse(JSON.stringify(self.rooms))
                                rooms.push(self.encodeRooms([response.data])[0])
                                self.rooms = rooms
                                self.applySearchRoomsValue()
                                setTimeout(function () {
                                    self.openRoom(response.data.id)
                                })
                            } else {
                                self.$isLoading(false)
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }
                }
            },
            newMessage: function (args) {
                const self = this
                this.draftInputValue = ''

                if (!args.payload || (args.payload && args.payload.type !== this.filesTypes.text && (typeof args.payload.media === 'string' && (args.payload.media.indexOf('data:video/') > -1 || args.payload.media.indexOf('data:application/pdf;') > -1)))) {
                    return this.$alert(
                        window.strings['invalid_file'],
                        '',
                        'warning',
                        Utils.getAlertOptions()
                    )
                }

                if (this.chatUser && this.chatUser.id) {
                    args.payload.client_id = this.chatUser.id
                }

                Api.chat.newMessage(args.payload, function (response) {
                    if (response.success) {
                        const messages = JSON.parse(JSON.stringify(self.messages))
                        messages.push(self.encodeMessages([response.data])[0])
                        self.refreshChatMessages(messages)

                        if (args.callback) {
                            args.callback()
                        }
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            getMessages: function (payload, callback) {
                const self = this

                if (this.requestsProcessing.getMessages) {
                    finish()
                    return false
                }

                this.requestsProcessing.getMessages = true

                Api.chat.getMessages(payload, function (response) {
                    delete self.requestsProcessing.getMessages

                    if (response.success) {
                        // Recebe novas mensagens
                        if (payload.new_messages) {
                            let messages = []
                            messages = JSON.parse(JSON.stringify(self.messages))

                            const newMessages = self.encodeMessages(response.data)
                            for (let i = 0; i < newMessages.length; i++) {
                                const oldMessageIndex = messages.findIndex(function (item) {
                                    return item._id === newMessages[i]._id
                                })
                                if (oldMessageIndex > -1) {
                                    messages[oldMessageIndex] = newMessages[i]
                                } else {
                                    messages.push(newMessages[i])
                                }
                            }

                            messages = self.removeMessagesDuplicated(messages)
                            self.refreshChatMessages(messages)
                            finish()
                            return true
                        }

                        // Recebe mensagens antigo (scroll para cima)
                        if (payload.message_id) {
                            let messages = []
                            messages = JSON.parse(JSON.stringify(self.messages))
                            messages = self.encodeMessages(response.data).concat(messages)
                            messages = self.removeMessagesDuplicated(messages)
                            self.refreshChatMessages(messages)
                            finish()
                            return true
                        }

                        self.refreshChatMessages(self.encodeMessages(response.data, true))
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }

                    finish()
                })

                function finish() {
                    if (callback) {
                        callback()
                    }
                }
            },
            onOpenFile: function (data) {
                const self = this
                const message = data.message

                if (message.files[0] && message.files[0].url) {
                    if (message.files[0].audio) {
                        return false
                    }
                    const a = document.createElement('a')
                    a.href = message.files[0].url
                    a.download = 'image.' + message.files[0].type
                    a.click()
                    return true
                }

                const messageId = parseInt(message._id)
                let messages = JSON.parse(JSON.stringify(self.messages))
                const index = messages.findIndex(function (msg) {
                    return msg._id === messageId
                })
                if (index > -1) {
                    messages[index].isLoading = true
                    self.refreshChatMessages(messages)
                }

                if (message.type === this.filesTypes.video) {
                    if (message.message_aux && message.message_aux.video_id) {
                        this.getMessageVideo(message.message_aux.video_id, function () {
                            refreshMessage()
                        })
                    } else {
                        messages[index].isLoading = false
                        this.refreshChatMessages(messages)
                        this.$alert(
                            window.strings['invalid_file'],
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                    return true
                }

                refreshMessage()

                function refreshMessage() {
                    Api.chat.getMessage({
                        user_id: self.chatUser.chat_id,
                        chat_id: parseInt(message.chat_id),
                        id: messageId,
                        read: false,
                    }, function (response) {
                        if (self.getActiveRoomId() && response.success && response.data && response.data[0]) {
                            let messages = JSON.parse(JSON.stringify(self.messages))
                            const index = messages.findIndex(function (msg) {
                                return msg._id === messageId
                            })
                            if (index > -1) {
                                messages[index] = self.encodeMessages([response.data[0]])[0]
                                messages = self.removeMessagesDuplicated(messages)
                                self.refreshChatMessages(messages)
                                if (data.scrollToEnd) {
                                    self.applyScrollToEnd()
                                }
                                self.checkOpenPreviewFile()
                            }
                        }
                    })
                }
            },
            onPreviewFile: function (data) {
                this.previewFileOpened = data && data.message ? data.message : null
            },
            getNewMessagesCount: async function (callback) {
                const self = this

                if (!this.chatUser || (this.chatUser && !this.chatUser.chat_id)) {
                    finish()
                    return false
                }

                if (this.requestsProcessing.getNewMessagesCount) {
                    finish()
                    return false
                }

                if (this.getActiveRoomId()) {
                    finish()
                    return false
                }

                this.requestsProcessing.getNewMessagesCount = true

                if (this.chatUser && this.chatUser.chat_id) {
                    Api.chat.getNewMessagesCount(this.chatUser.chat_id, function (response) {
                        delete self.requestsProcessing.getNewMessagesCount

                        if (response.success) {
                            if (response.data && response.data.details) {
                                if (window.lastGetNewMessagesCount === JSON.stringify(response.data.details)) {
                                    finish()
                                    return false
                                }
                                window.lastGetNewMessagesCount = JSON.stringify(response.data.details)
                                const rooms = JSON.parse(JSON.stringify(self.rooms))
                                const roomsIds = []

                                for (let i = 0; i < rooms.length; i++) {
                                    const room = rooms[i]
                                    const detail = response.data.details.find(function (dt) {
                                        return dt.chat_id === room.roomId
                                    })
                                    roomsIds.push(room.roomId)
                                    if (detail) {
                                        room.unreadCount = detail.unread_count ? detail.unread_count : 0
                                        const lastMessageDate = Utils.convertUtcDate(detail.last_msg_dt, true)
                                        room.lastMessageDate = lastMessageDate
                                        room.lastMessage = {
                                            content: '',
                                            distributed: detail.last_msg_status === self.messageStatus.delivered || detail.last_msg_status === self.messageStatus.read ? true : false,
                                            seen: detail.last_msg_status === self.messageStatus.read ? true : false,
                                            userId: detail.last_msg_user_id,
                                            timestamp: lastMessageDate,
                                        }
                                    } else {
                                        room.unreadCount = 0
                                    }
                                }

                                const roomsNotFound = response.data.details.filter(function (dt) {
                                    return roomsIds.indexOf(dt.chat_id) === -1
                                })
                                if (roomsNotFound.length) {
                                    self.getRooms()
                                }

                                self.rooms = rooms

                                setTimeout(function () {
                                    self.applySearchRoomsValue()
                                    finish()
                                })

                                return true
                            }
                        } else {
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }

                        finish()
                    })
                } else {
                    finish()
                }

                function finish() {
                    if (callback) {
                        callback()
                    }
                }
            },
            refreshRoomsNames: async function (callback) {
                const self = this

                try {
                    if (!this.chatUser || (this.chatUser && !this.chatUser.chat_id)) {
                        finish()
                        return false
                    }

                    if (this.requestsProcessing.refreshRoomsNames) {
                        finish()
                        return false
                    }

                    const newRegistersIds = []
                    for (let i = 0; i < this.rooms.length; i++) {
                        if (this.rooms[i].roomName.indexOf(this.newClientKey) > -1) {
                            newRegistersIds.push(this.rooms[i].roomId)
                        }
                    }

                    if (!newRegistersIds.length) {
                        finish()
                        return false
                    }

                    this.requestsProcessing.refreshRoomsNames = true

                    Api.chat.getChatsNames({
                        ids: newRegistersIds
                    }, function (response) {
                        delete self.requestsProcessing.refreshRoomsNames

                        if (response.success) {
                            if (window.lastGetChatsNames === JSON.stringify(response.data)) {
                                finish()
                                return false
                            }
                            window.lastGetChatsNames = JSON.stringify(response.data)
                            const rooms = JSON.parse(JSON.stringify(self.rooms))
                            let updated = false
                            for (let i = 0; i < response.data.length; i++) {
                                const rInd = rooms.findIndex(function (rm) {
                                    return rm.roomId === response.data[i].id
                                })
                                if (rInd > -1 && rooms[rInd].roomName !== response.data[i].name) {
                                    rooms[rInd].roomName = response.data[i].name
                                    updated = true
                                }
                            }
                            if (updated) {
                                self.rooms = rooms
                                self.applySearchRoomsValue()
                            }
                        }

                        finish()
                    })
                } catch {
                    finish()
                    return false
                }

                function finish() {
                    if (callback) {
                        callback()
                    }
                }
            },
            refreshRoomsDraftAndOpenedFlag: async function (callback) {
                const self = this

                if (!this.draftEnabled) {
                    finish()
                    return false
                }

                if (!this.chatUser || (this.chatUser && !this.chatUser.chat_id)) {
                    finish()
                    return false
                }

                if (this.requestsProcessing.refreshRoomsDraftAndOpenedFlag) {
                    finish()
                    return false
                }

                this.requestsProcessing.refreshRoomsDraftAndOpenedFlag = true

                Api.chat.getChatsDraftsAndOpenedFlag({
                    id: self.chatUser.chat_id
                }, function (response) {
                    delete self.requestsProcessing.refreshRoomsDraftAndOpenedFlag

                    if (response.success) {
                        const rooms = JSON.parse(JSON.stringify(self.rooms))
                        let updated = false
                        for (let i = 0; i < response.data.length; i++) {
                            const rInd = rooms.findIndex(function (rm) {
                                return rm.roomId === response.data[i].id
                            })
                            if (rInd > -1 && rooms[rInd].draft !== response.data[i].draft) {
                                rooms[rInd].draft = response.data[i].draft
                                updated = true
                            }
                            if (rInd > -1 && rooms[rInd].opened !== response.data[i].opened) {
                                rooms[rInd].opened = response.data[i].opened
                                updated = true
                            }
                        }
                        if (updated) {
                            self.rooms = rooms
                            self.applySearchRoomsValue()
                        }
                    }

                    finish()
                })

                function finish() {
                    if (callback) {
                        callback()
                    }
                }
            },
            refreshRoomMessages: async function (callback) {
                const activeRoomId = this.getActiveRoomId()

                if (this.chatUser && this.chatUser.chat_id && activeRoomId) {
                    if (this.markAsUnreadCache &&
                        this.markAsUnreadCache.activeRoom !== activeRoomId
                    ) {
                        this.markAsUnreadCache = null
                    }

                    if (this.messages) {
                        let lastMessage = this.messages[this.messages.length - 1]
                        let lastMessageIndex = this.messages.findIndex(function (item) {
                            return !item.seen
                        })

                        if (lastMessageIndex > -1 && this.messages[lastMessageIndex - 1]) {
                            lastMessage = this.messages[lastMessageIndex - 1]
                        }

                        let lastId = lastMessage ? lastMessage._id : 0
                        if (this.markAsUnreadCache) {
                            lastId += 1
                        }

                        return this.getMessages({
                            user_id: this.chatUser.chat_id,
                            chat_id: activeRoomId,
                            limit: this.getMessagesLimitParameter,
                            new_messages: true,
                            include_message_id: true,
                            message_id: lastId
                        }, callback)
                    }
                }

                if (callback) {
                    callback()
                }
            },

            // Active promises to refresh chat (rooms names, new messages)
            activeAutoRefreshPromises: async function () {
                const self = this

                clearTimeout(newMessagesPromise)
                clearTimeout(roomNamesPromise)
                clearTimeout(newRoomMessagesPromise)

                if (window.isPT) {
                    // Verificar se recebeu novas mensagens nas outras conversas
                    startNewMessagesPromise()
                    // Verificar se os nomes das conversas dos novos registos foram atualizados
                    startRoomNamesPromise()
                }

                // Obter mensagens novas do chat que está aberto
                startNewRoomMessagesPromise()

                function startNewMessagesPromise() {
                    clearTimeout(newMessagesPromise)
                    newMessagesPromise = setTimeout(function () {
                        if (!checkSocketsStatus()) {
                            self.getNewMessagesCount(function () {
                                startNewMessagesPromise()
                            })
                        }
                    }, self.newMessagesPromiseTime)
                }

                function startRoomNamesPromise() {
                    clearTimeout(roomNamesPromise)
                    if (self.changeRoomNameEnabled) {
                        roomNamesPromise = setTimeout(function () {
                            if (!checkSocketsStatus()) {
                                self.refreshRoomsNames(function () {
                                    startRoomNamesPromise()
                                })
                            }
                        }, self.roomNamesPromiseTime)
                    }
                }

                function startNewRoomMessagesPromise() {
                    clearTimeout(newRoomMessagesPromise)
                    newRoomMessagesPromise = setTimeout(function () {
                        if (!checkSocketsStatus()) {
                            self.refreshRoomMessages(function () {
                                startNewRoomMessagesPromise()
                            })
                        }
                    }, self.newRoomMessagesPromiseTime)
                }

                function checkSocketsStatus() {
                    if (window.socketsEnabled) {
                        clearTimeout(newMessagesPromise)
                        clearTimeout(roomNamesPromise)
                        clearTimeout(newRoomMessagesPromise)
                        return true
                    }
                    return false
                }
            },

            // Active promises to refresh chats draft
            activeAutoRefreshDraftsPromise: async function () {
                const self = this
                clearTimeout(refreshDraftsPromise)
                startRefreshDraftsPromise()

                function startRefreshDraftsPromise() {
                    clearTimeout(refreshDraftsPromise)
                    if (self.draftEnabled) {
                        refreshDraftsPromise = setTimeout(function () {
                            self.refreshRoomsDraftAndOpenedFlag(function () {
                                startRefreshDraftsPromise()
                            })
                        }, self.refreshDraftsPromiseTime)
                    }
                }
            },

            // Active firebase notifications to refresh chat (rooms names, new messages)
            activeAutoRefreshNotifications: async function () {
                const self = this
                window.addEventListener('message', function (event) {
                    try {
                        if (event && event.data) {
                            const data = JSON.parse(event.data)
                            if (window.chatDisabled && data && data.event !== 'app-enable-chat') {
                                return false
                            }
                            if (data && data.event === 'chat-notification' && data.data) {
                                const action = data.data
                                if (action && action.data) {
                                    if (action.id === 'msg-created') {
                                        if (self.getActiveRoomId() === action.data.chat_id) {
                                            self.refreshRoomMessages()
                                        }
                                        return true
                                    }
                                    if (action.id === 'msg-updated') {
                                        refreshMessage(action.data.chat_id, action.data.id)
                                        return true
                                    }
                                    if (action.id === 'msg-deleted') {
                                        deleteMessage(action.data.chat_id, action.data.id)
                                        return true
                                    }
                                    if (!self.singleRoom && action.id === 'chat-refresh') {
                                        refreshChat(action.data)
                                        return true
                                    }
                                    if (!self.singleRoom && action.id === 'chat-delete') {
                                        self.deleteChat(action.data.id)
                                        return true
                                    }
                                    if (self.pagesEmployeesEnabled && action.id === 'page-refresh') {
                                        refreshPageEmployee(action.data)
                                        return true
                                    }
                                    if (self.pendingFeedbacksEnabled && action.id === 'feedbacks') {
                                        self.pendingFeedbacks = action.data
                                        return true
                                    }
                                    if (self.roomFlagsEnabled && action.id === 'no-reply-warn') {
                                        refreshChatDangerStatus(action.data)
                                        return true
                                    }
                                }
                            }
                            if (data && data.event === 'app-refresh-chat') {
                                handleRefreshChatAppEvent()
                            }
                            if (data && data.event === 'app-enable-chat') {
                                handleEnableAppEvent()
                            }
                            if (data && data.event === 'app-disable-chat') {
                                handleDisableAppEvent()
                            }
                            if (data && data.event === 'app-check-enable') {
                                handleCheckEnableAppEvent()
                            }
                        }

                        function refreshMessage(chatId, messageId) {
                            if (self.getActiveRoomId() === chatId) {
                                const messageMarkedAsUnread = self.markAsUnreadCache && self.markAsUnreadCache.activeRoom === chatId && self.markAsUnreadCache.messageId === messageId ? true : false
                                const payload = {
                                    user_id: self.chatUser.chat_id,
                                    chat_id: chatId,
                                    id: messageId,
                                    read: !messageMarkedAsUnread ? true : false,
                                }
                                Api.chat.getMessage(payload, function (response) {
                                    if (response.success) {
                                        if (self.getActiveRoomId() !== chatId) {
                                            return false
                                        }
                                        if (!response.data || (response.data && !response.data[0])) {
                                            return false
                                        }
                                        let messages = JSON.parse(JSON.stringify(self.messages))
                                        const index = messages.findIndex(function (msg) {
                                            return msg._id === messageId
                                        })
                                        if (index > -1) {
                                            messages[index] = self.encodeMessages([response.data[0]])[0]
                                            messages = self.removeMessagesDuplicated(messages)
                                            self.refreshChatMessages(messages)
                                            return true
                                        }
                                    } else {
                                        self.$alert(
                                            response.message,
                                            '',
                                            'warning',
                                            Utils.getAlertOptions()
                                        )
                                    }
                                })
                            }
                        }

                        function deleteMessage(chatId, messageId) {
                            if (self.getActiveRoomId() === chatId) {
                                let messages = JSON.parse(JSON.stringify(self.messages))
                                const index = messages.findIndex(function (msg) {
                                    return msg._id === messageId
                                })
                                if (index > -1) {
                                    messages.splice(index, 1)
                                    messages = self.removeMessagesDuplicated(messages)
                                    self.refreshChatMessages(messages)
                                    return true
                                }
                            }
                        }

                        function refreshChat(chatData) {
                            if (chatData) {
                                const index = self.rooms.findIndex(function (rm) {
                                    return rm.roomId === chatData.id
                                })
                                const room = self.encodeRooms([chatData])[0]
                                if (index > -1) {
                                    if (room.unreadCount === undefined) {
                                        room.unreadCount = self.rooms[index].unreadCount
                                    }
                                    self.rooms[index] = room
                                } else {
                                    if (room.unreadCount === undefined) {
                                        room.unreadCount = 0
                                    }
                                    self.rooms.push(room)
                                }
                                callRefreshChats()
                            }
                        }

                        function refreshPageEmployee(page) {
                            try {
                                if (page) {
                                    self.pagesEmployees[page.number] = JSON.parse(page.label)
                                    self.$forceUpdate()
                                }
                            } catch { }
                        }

                        function refreshChatDangerStatus(chatData) {
                            if (chatData && chatData.length) {
                                for (let r = 0; r < self.rooms.length; r++) {
                                    if (chatData.indexOf(self.rooms[r].roomId) > -1) {
                                        self.rooms[r].danger = true
                                    }
                                }
                                callRefreshChats()
                            }
                        }

                        function callRefreshChats() {
                            if (!self.refreshChatPromiseStarted) {
                                self.refreshChatPromiseStarted = true
                                clearTimeout(self.refreshChatPromise)
                                self.refreshChatPromise = setTimeout(function () {
                                    self.refreshChatPromiseStarted = false
                                    self.applySearchRoomsValue()
                                }, self.refreshChatPromiseTime)
                            }
                        }
                    } catch { }
                })

                // events from android (to work in android we need to use document)
                document.addEventListener('message', function (event) {
                    try {
                        if (event && event.data) {
                            const data = JSON.parse(event.data)
                            if (data && data.event === 'app-refresh-chat') {
                                handleRefreshChatAppEvent()
                            }
                            if (data && data.event === 'app-enable-chat') {
                                handleEnableAppEvent()
                            }
                            if (data && data.event === 'app-disable-chat') {
                                handleDisableAppEvent()
                            }
                            if (data && data.event === 'app-check-enable') {
                                handleCheckEnableAppEvent()
                            }
                        }
                    } catch { }
                })

                function handleRefreshChatAppEvent() {
                    self.refreshRoomMessages()
                }

                function handleEnableAppEvent() {
                    window.chatDisabled = false
                    self.markAsUnreadCache = null
                    self.refreshRoomMessages()
                    self.sendEventToApp('app-enable-chat-done')
                }

                function handleDisableAppEvent() {
                    window.chatDisabled = true
                    self.sendEventToApp('app-disable-chat-done')
                }

                function handleCheckEnableAppEvent() {
                    self.sendEventToApp('app-check-enable-done')
                }
            },

            // Auto reset with timeout
            activeAutoRefreshMessagesCount: async function () {
                const self = this
                clearTimeout(autoRefreshMessagesCountPromise)
                autoRefreshMessagesCountPromise = setTimeout(function () {
                    self.refreshChat(true)
                    self.activeAutoRefreshMessagesCount()
                }, self.autoRefreshMessagesCountPromiseTime)
            },

            // Encodes
            encodeRooms: function (data) {
                const enumsTags = this.tagsEnabled ? Utils.getUserTags().map(a => a.value) : null
                const rooms = []

                for (let i = 0; i < data.length; i++) {
                    const roomNotes = getNotes(data[i].notes)
                    if (this.filterEmployeeEnabled && window.logId && window.logId !== window.chatUserPtId &&
                        ((roomNotes && roomNotes.employees && roomNotes.employees.indexOf(window.logId) === -1) ||
                        (roomNotes && !roomNotes.employees) ||
                        (!roomNotes))
                    ) {
                        continue
                    }
                    const lastMessageDate = Utils.convertUtcDate(data[i].last_msg_utc, true)
                    const roomFlags = getRoomsFlags(data[i].flags)
                    let roomTags = this.tagsEnabled && data[i].tags ? data[i].tags.split(',') : []
                    if (this.tagsEnabled && roomTags.length) {
                        roomTags = roomTags.filter(function (tag) {
                            return enumsTags.indexOf(tag) > -1
                        })
                    }
                    rooms.push({
                        roomId: data[i].id,
                        roomName: data[i].name,
                        unreadCount: (data[i].admin_unread || data[i].admin_unread === 0) && this.useRoomAdminUnread ? data[i].admin_unread : data[i].unread_count,
                        fixed: data[i].fixed,
                        lastMessageDate: lastMessageDate,
                        draft: this.draftEnabled ? data[i].draft : '',
                        opened: data[i].opened,
                        lastMessage: {
                            content: '',
                            distributed: data[i].last_msg_status === this.messageStatus.delivered || data[i].last_msg_status === this.messageStatus.read ? true : false,
                            seen: data[i].last_msg_status === this.messageStatus.read ? true : false,
                            userId: data[i].last_msg_user_id,
                            timestamp: lastMessageDate,
                        },
                        users: [{
                            _id: data[i].users[1],
                            username: data[i].name,
                        }],
                        notes: roomNotes,
                        archived: data[i].archived,
                        tags: roomTags,
                        danger: this.roomFlagsEnabled && roomFlags.no_reply_warn ? true : false,
                        category: data[i].category !== 'X' ? data[i].category : '',
                        categoryProcessing: data[i].category === 'X' ? true : false
                    })
                }

                return rooms

                function getNotes(notes) {
                    try {
                        if (notes) {
                            const notesData = JSON.parse(notes)
                            if (notesData.accepted_foods) {
                                notesData.accepted_foods = [...new Set(notesData.accepted_foods)]
                            }
                            if (notesData.unaccepted_foods) {
                                notesData.unaccepted_foods = [...new Set(notesData.unaccepted_foods)]
                            }
                            if (notesData.intolerant_foods) {
                                notesData.intolerant_foods = [...new Set(notesData.intolerant_foods)]
                            }
                            return notesData
                        }
                    } catch { }
                    return null
                }

                function getRoomsFlags(flags) {
                    const noReplyWarn = (flags & (1 << 0)) !== 0
                    return {
                        no_reply_warn: noReplyWarn
                    }
                }
            },
            encodeMessages: function (data, checkDisableActions) {
                let messages = []

                if (!data || (data && !data.length)) {
                    return messages
                }

                const chatIdActive = !this.singleRoom ? this.getActiveRoomId() : null

                for (let i = 0; i < data.length; i++) {

                    if (!this.singleRoom && data[i].chat_id !== chatIdActive) {
                        continue
                    }

                    try {
                        if (data[i].message_aux && typeof data[i].message_aux === 'string') {
                            data[i].message_aux = JSON.parse(data[i].message_aux)
                        }
                    } catch { }

                    const message = {
                        chat_id: data[i].chat_id,
                        _id: data[i].id,
                        content: data[i].message,
                        senderId: data[i].user_id,
                        date: Utils.convertUtcDate(data[i].date_utc, false, 'DD-MM-YYYY'),
                        timestamp: Utils.convertUtcDate(data[i].date_utc, false, 'HH:mm'),
                        distributed: data[i].status === this.messageStatus.delivered || data[i].status === this.messageStatus.read ? true : false,
                        seen: data[i].status === this.messageStatus.read ? true : false,
                        files: [],
                        type: data[i].type,
                        message_aux: data[i].message_aux,
                        pinned: data[i].pinned ? data[i].pinned : 0,
                        disableActions: !window.isPT && checkDisableActions && this.chatUser.chat_id === data[i].user_id && (i > 0 || data[i].status === this.messageStatus.read) ? true : false,
                        // replyMessage: data[i].replyMessage
                    }

                    if (data[i].type !== this.filesTypes.text) {
                        const file = {}
                        const cacheKey = data[i].chat_id + '_' + data[i].id
                        let media = ''
                        if (data[i].type === this.filesTypes.video) {
                            if (data[i].message_aux && data[i].message_aux.video_id) {
                                if (this.messagesVideosCache[data[i].message_aux.video_id]) {
                                    media = this.encodeVideoBase64(this.messagesVideosCache[data[i].message_aux.video_id])
                                }
                                file.video_id = data[i].message_aux.video_id
                            } else {
                                continue
                            }
                        } else {
                            media = data[i].media ? data[i].media : this.getCacheMedia(cacheKey)
                        }
                        if (media) {
                            file.audio = data[i].type === this.filesTypes.audio
                            if (data[i].type === this.filesTypes.video) {
                                file.type = media.indexOf('video/mp4') > -1 ? 'mp4' : 'webm'
                            } else {
                                file.type = data[i].type === this.filesTypes.audio ? 'mp3' : 'png'
                            }
                            file.url = media
                            if (data[i].media && data[i].type !== this.filesTypes.video) {
                                this.setCacheMedia(cacheKey, data[i].media)
                                if (file.type === 'png') {
                                    file.type = data[i].media.indexOf('data:image/png') > -1 ? 'png' : 'jpg'
                                }
                            }
                        }
                        message.files.push(file)
                    }

                    messages.push(message)
                }

                messages = messages.sort(function (a, b) {
                    const x = a._id
                    const y = b._id
                    return ((x < y) ? -1 : ((x > y) ? 1 : 0))
                })

                return messages
            },
            removeMessagesDuplicated: function (messages) {
                const ids = []
                for (let i = messages.length - 1; i > -1; i--) {
                    if (ids.indexOf(messages[i]._id) > -1) {
                        messages.splice(i, 1)
                    }
                    ids.push(messages[i]._id)
                }
                return messages
            },

            // Chat events
            loadRoomMoreMessages: function () {
                const roomId = this.getActiveRoomId()
                if (roomId) {
                    delete this.requestsProcessing.getMessages
                    this.getMessages({
                        user_id: this.chatUser.chat_id,
                        chat_id: roomId,
                        limit: this.getMessagesLimitParameter,
                        message_id: this.messages[0]._id
                    })
                }
            },
            onFetchMessages: function (data) {
                const self = this
                data = JSON.parse(JSON.stringify(data))
                const roomId = parseInt(data.room.roomId)

                this.fixedMessages = null
                this.messagesLoaded = false
                this.refreshChatMessages([])
                delete this.requestsProcessing.getMessages

                this.getMessages({
                    user_id: this.chatUser.chat_id,
                    chat_id: roomId,
                    limit: this.getMessagesLimitParameter
                }, function () {
                    self.messagesLoaded = true
                    self.refreshRoom({
                        roomId: roomId
                    })
                    self.sendNewMessagesCountToDashboard()
                    self.setRoomOpened(roomId)
                    self.setRoomDraftMessageInput(roomId, data.room.draft)
                    self.setPageEmployee()
                    self.startAutoLoadMessagesFiles()
                    self.getFixedMessages()
                    self.getRoomUser()
                    self.openRoomSummaryNotes()
                })
            },
            refreshRoom: function (args) {
                try {
                    const self = this
                    const roomId = args.roomId ? args.roomId : this.getActiveRoomId()
                    const markAsUnread = args.markAsUnread ? true : false
                    let lastMessage = null
                    let lastMessageDate = null

                    if (roomId) {
                        if (markAsUnread) {
                            lastMessage = this.messages && this.messages.length ? this.messages[this.messages.length - 1] : null
                        } else {
                            lastMessageDate = this.messages && this.messages.length ? this.messages.map(function (msg) { return msg.date }).sort().reverse()[0] : null
                        }
                        setRoom(this.rooms.find(function (item) {
                            return item.roomId === roomId
                        }))
                        setRoom(this.filteredRooms.find(function (item) {
                            return item.roomId === roomId
                        }))
                    }

                    function setRoom(room) {
                        if (room) {
                            if (lastMessageDate) {
                                room.unreadCount = 0
                                if (lastMessageDate) {
                                    room.lastMessageDate = lastMessageDate
                                    room.lastMessage = {
                                        content: '',
                                        distributed: room.lastMessage.distributed,
                                        seen: room.lastMessage.seen,
                                        userId: room.lastMessage.userId,
                                        timestamp: lastMessageDate
                                    }
                                }
                                return true
                            }
                            if (lastMessage) {
                                room.unreadCount = self.messages.filter(function (m) {
                                    return !m.seen
                                }).length
                                room.lastMessage.seen = lastMessage.seen
                                return true
                            }
                        }
                    }
                } catch { }
            },
            startAutoLoadMessagesFiles: function () {
                if (this.autoLoadMessagesFiles) {
                    let counter = 0
                    for (let i = this.messages.length - 1; i > -1; i--) {
                        if (this.messages[i].type !== this.filesTypes.text && this.messages[i].type !== this.filesTypes.video && this.messages[i].files && this.messages[i].files[0] && !this.messages[i].files[0].url) {
                            counter += 1
                            this.onOpenFile({
                                message: this.messages[i],
                                scrollToEnd: true
                            })
                        }
                        if (counter >= this.autoLoadMessagesFilesLimit) {
                            break
                        }
                    }
                }
            },
            getFixedMessages: function (removeLoading) {
                const self = this
                if (this.fixMessageEnabled) {
                    const activeRoomId = this.getActiveRoomId()
                    if (activeRoomId) {
                        const messagesList = this.getChatHtmlElement('messages-list')
                        if (messagesList) {
                            messagesList.style.marginTop = ''
                        }
                        Api.chat.getFixedMessages({
                            user_id: this.chatUser.chat_id,
                            chat_id: activeRoomId,
                        }, function (response) {
                            if (response.success && response.data && response.data.length) {
                                self.fixedMessages = self.encodeMessages(response.data)
                                if (messagesList) {
                                    messagesList.style.marginTop = '200px'
                                }
                            } else {
                                self.fixedMessages = null
                            }
                            if (removeLoading) {
                                self.$isLoading(false)
                            }
                        })
                    }
                }
            },
            openFixedMessage: function (message) {
                const self = this
                const activeRoomId = this.getActiveRoomId()
                if (activeRoomId) {
                    if (message && message.files && message.files[0] && message.files[0].url) {
                        self.dialogFixedMessageData = message
                        self.dialogFixedMessage = true
                        return true
                    }
                    this.$isLoading(true)
                    Api.chat.getMessage({
                        user_id: self.chatUser.chat_id,
                        chat_id: activeRoomId,
                        id: message._id,
                        read: false,
                    }, function (response) {
                        self.$isLoading(false)
                        if (response.success && response.data && response.data[0]) {
                            self.dialogFixedMessageData = self.encodeMessages([response.data[0]])[0]
                            self.dialogFixedMessage = true
                        } else {
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }
            },
            closeFixedMessage: function () {
                this.dialogFixedMessage = false
                this.dialogFixedMessageData = null
            },
            getRoomUser: function () {
                const self = this
                if (this.showNotes) {
                    this.roomUser = null
                    this.getRoomClient({
                        fields: ['pt_id', 'db_id', 'id', 'notes', 'paym_sub', 'country', 'flags', 'app_version'],
                        updateFlags: true,
                        callback: function (client) {
                            try {
                                if (client.app_version >= 110000) {
                                    return self.$alert(
                                        'Cliente já não usa este Chat. Para comunicar com este cliente, utiliza o Chat TG 2.0 por favor.',
                                        '',
                                        'warning',
                                        Utils.getAlertOptions()
                                    ).then(() => {
                                        self.closeRoom()
                                    })
                                }
                                if (client) {
                                    if (client.notes && typeof client.notes === 'string') {
                                        client.notes = JSON.parse(client.notes)
                                    }
                                    if (client.flags) {
                                        client.has_training_plan = (client.flags & (1 << 7)) !== 0
                                        client.has_food_plan = (client.flags & (1 << 8)) !== 0
                                    }
                                    self.roomUser = client
                                }
                            } catch { }
                        }
                    })
                }
            },
            openRoomSummaryNotes: function () {
                const self = this
                if (this.roomSummaryNotesEnabled) {
                    this.dialogRoomLastFeedbackDate = ''
                    const roomIdActive = this.getActiveRoomId()
                    if (roomIdActive) {
                        if (this.roomSummaryNotesCache && this.roomSummaryNotesCache[roomIdActive]) {
                            this.dialogRoomSummaryNotes = this.roomSummaryNotesCache[roomIdActive]
                        } else {
                            Api.chat.getRoomNotes(roomIdActive, function (response) {
                                if (response.success && response.data && response.data.length && self.getActiveRoomId()) {
                                    for (let i = 0; i < response.data.length; i++) {
                                        response.data[i].notes = JSON.parse(response.data[i].notes)
                                    }
                                    if (!self.roomSummaryNotesCache) {
                                        self.roomSummaryNotesCache = {}
                                    }
                                    self.roomSummaryNotesCache[roomIdActive] = response.data
                                    self.dialogRoomSummaryNotes = response.data
                                }
                            })
                        }
                        this.getRoomClient({
                            fields: ['db_id'],
                            callback: function (client) {
                                if (client && client.db_id) {
                                    Api.pt.getLastPhysicalEvaluation({
                                        id: client.db_id,
                                        fields: ['date']
                                    }, function (response) {
                                        if (response.success && response.data && response.data[0]) {
                                            self.dialogRoomLastFeedbackDate = response.data[0].date
                                        }
                                    })
                                }
                            }
                        })
                    }
                }
            },
            closeRoomSummaryNotes: function () {
                this.dialogRoomSummaryNotes = null
            },
            selectPtClient: function (client) {
                const self = this
                this.handleRegisterUser({
                    user: client,
                    callback: function (registerUser) {
                        self.openChat({
                            chat_id: client.chat_id,
                            users: [self.chatUser.chat_id, registerUser.id]
                        })
                    }
                })
            },
            onSendMessage: function (data) {
                const self = this

                // if (data.replyMessage) {
                //     return this.onEditMessage(null, {
                //         id: parseInt(data.replyMessage._id),
                //         replyMessage: {
                //             user_id: this.chatUser.chat_id,
                //             type: this.filesTypes.text,
                //             message: data.content
                //         }
                //     })
                // }

                const roomId = parseInt(data.roomId)
                const room = this.rooms.find(function (item) {
                    return item.roomId === roomId
                })

                if (data.content) {
                    if (data.files && data.files.length) {
                        sendFiles()
                    } else {
                        this.newMessage({
                            payload: {
                                chat_id: room.roomId,
                                user_id: this.chatUser.chat_id,
                                type: this.filesTypes.text,
                                message: data.content
                            }
                        })
                    }
                } else {
                    sendFiles()
                }

                function sendFiles() {
                    if (data.files) {
                        let messageTextAdded = false
                        for (let i = 0; i < data.files.length; i++) {
                            self.getFileBase64(data.files[i], function (base64, type) {
                                self.newMessage({
                                    payload: {
                                        chat_id: room.roomId,
                                        user_id: self.chatUser.chat_id,
                                        type: type,
                                        message: data.content && !messageTextAdded ? data.content : '',
                                        media: base64,
                                        message_aux: type === self.filesTypes.video ? JSON.stringify({
                                            video_id: base64
                                        }) : null
                                    }
                                })
                                messageTextAdded = true
                            })
                        }
                    }
                }
            },
            onEditMessage: function (data) {
                const self = this
                const id = parseInt(data.messageId)
                this.draftInputValue = ''

                if (data.files && data.files.length) {
                    sendFiles()
                } else {
                    updateMessage({
                        id: id,
                        chat_id: parseInt(data.roomId),
                        user_id: this.chatUser.chat_id,
                        type: this.filesTypes.text,
                        message: data.newContent
                    })
                }

                function sendFiles() {
                    if (data.files) {
                        let messageTextAdded = false
                        for (let i = 0; i < data.files.length; i++) {
                            if (data.files[i].blob) {
                                self.getFileBase64(data.files[i], function (base64, type) {
                                    updateMessage({
                                        id: id,
                                        chat_id: parseInt(data.roomId),
                                        user_id: self.chatUser.chat_id,
                                        type: type,
                                        message: data.newContent && !messageTextAdded ? data.newContent : '',
                                        media: base64,
                                        message_aux: type === self.filesTypes.video ? JSON.stringify({
                                            video_id: base64
                                        }) : null
                                    })
                                    messageTextAdded = true
                                })
                            } else {
                                let type = self.filesTypes.image
                                if (data.files[i].audio) {
                                    type = self.filesTypes.audio
                                } else if (data.files[i].type !== 'png') {
                                    type = self.filesTypes.video
                                }
                                updateMessage({
                                    id: id,
                                    chat_id: parseInt(data.roomId),
                                    user_id: self.chatUser.chat_id,
                                    type: type,
                                    message: data.newContent && !messageTextAdded ? data.newContent : '',
                                    media: type === self.filesTypes.video ? data.files[i].video_id : data.files[i].url,
                                    message_aux: type === self.filesTypes.video ? JSON.stringify({
                                        video_id: data.files[i].video_id
                                    }) : null
                                })
                                messageTextAdded = true
                            }
                        }
                    }
                }

                function updateMessage(payload) {
                    Api.chat.updateMessage(payload, function (response) {
                        if (response.success) {
                            const messages = JSON.parse(JSON.stringify(self.messages))
                            for (let i = 0; i < messages.length; i++) {
                                if (messages[i]._id === id) {
                                    messages[i] = self.encodeMessages([response.data])[0]
                                    break
                                }
                            }
                            self.refreshChatMessages(messages)
                        }
                    })
                }
            },
            onDeleteMessageConfirmation: function (data) {
                const self = this
                this.$confirm(
                    this.str['delete_message_confirmation'],
                    '',
                    'warning',
                    Utils.getAlertOptions(true)
                ).then(() => {
                    self.onDeleteMessage(data)
                })
            },
            onDeleteMessage: function (data) {
                const self = this
                const payload = {
                    chat_id: parseInt(data.message.chat_id),
                    id: parseInt(data.message._id)
                }
                const id = payload.id
                const videoId = data.message.type === this.filesTypes.video && data.message.message_aux && data.message.message_aux.video_id ? data.message.message_aux.video_id : null

                Api.chat.deleteMessage(payload, function (response) {
                    if (response.success) {
                        const messages = JSON.parse(JSON.stringify(self.messages))
                        const index = messages.findIndex(function (item) {
                            return item._id === id
                        })
                        if (index > -1) {
                            messages.splice(index, 1)
                            self.refreshChatMessages(messages)
                        }
                        if (videoId) {
                            self.deleteMessageVideo(videoId)
                        }
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            onFixMessage: function (data) {
                const self = this
                const id = parseInt(data.message._id)
                Api.chat.updateMessage({
                    id: id,
                    chat_id: parseInt(data.roomId),
                    pinned: !data.message.pinned ? 1 : 0,
                }, function (response) {
                    if (response.success) {
                        const messages = JSON.parse(JSON.stringify(self.messages))
                        for (let i = 0; i < messages.length; i++) {
                            if (messages[i]._id === id) {
                                messages[i] = self.encodeMessages([response.data])[0]
                                break
                            }
                        }
                        self.getFixedMessages()
                        self.refreshChatMessages(messages)
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            unfixMessage: function (messageId) {
                const self = this
                const roomIdActive = this.getActiveRoomId()
                if (roomIdActive) {
                    this.$isLoading(true)
                    const id = parseInt(messageId)
                    Api.chat.updateMessage({
                        id: id,
                        chat_id: roomIdActive,
                        pinned: 0,
                    }, function (response) {
                        if (response.success) {
                            const messages = JSON.parse(JSON.stringify(self.messages))
                            for (let i = 0; i < messages.length; i++) {
                                if (messages[i]._id === id) {
                                    messages[i] = self.encodeMessages([response.data])[0]
                                    break
                                }
                            }
                            self.getFixedMessages(true)
                            self.refreshChatMessages(messages)
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }
            },
            onSaveFeedbackMessage: function (data) {
                const self = this
                if (data && data.message && data.message.content) {
                    this.$isLoading(true)
                    this.getRoomClient({
                        fields: ['db_id', 'id', 'pt_id', 'tags'],
                        callback: function (client) {
                            if (client && client.db_id) {
                                getFeedback(client, function (feedback) {
                                    updateFeedback(client, feedback, function () {
                                        updateUserTags(client)
                                    })
                                })
                            }
                        }
                    })
                }

                function getFeedback(client, callback) {
                    Api.pt.getLastPhysicalEvaluation({
                        id: client.db_id
                    }, function (response) {
                        if (response.success && response.data && response.data[0]) {
                           callback(response.data[0])
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                getResponse.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }

                function updateFeedback(client, feedback, callback) {
                    let newNotes = feedback.notes ? feedback.notes : ''
                    if (newNotes.indexOf(data.message.content) > -1) {
                        return callback()
                    }
                    const textToAdd = '-> ' + window.strings['message_sent_on'] + ' ' + data.message.date + ' ' + data.message.timestamp + ': "' + data.message.content + '"'
                    newNotes += newNotes ? ('\n\n' + textToAdd) : textToAdd
                    Api.pt.updatePhysicalEvaluation({
                        client_id: client.db_id,
                        id: feedback.id,
                        notes: newNotes
                    }, function (response) {
                        if (response.success) {
                            callback()
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }

                function updateUserTags(client) {
                    if (client.tags && client.tags.indexOf('TAG') > -1) {
                        let tags = client.tags.split(',')
                        tags.splice(tags.indexOf('TAG'), 1)
                        tags = tags.join(',')
                        Api.pt.updateUser({
                            id: client.id,
                            pt_id: client.pt_id,
                            tags: tags
                        }, function (response) {
                            self.$isLoading(false)
                            if (response.success) {
                                self.updateRoom('tags', tags)
                            } else {
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    } else {
                        self.$isLoading(false)
                    }
                }
            },
            onChangeSearchInput: function () {
                const self = this
                clearTimeout(this.searchPromise)
                this.searchPromise = setTimeout(function () {
                    self.applySearchRoomsValue()
                }, 500)
            },
            applySearchRoomsValue: function (force) {
                const self = this
                const roomIdActive = this.getActiveRoomId()
                if (!force && roomIdActive) {
                    return false
                }
                let filtered = []
                main()

                function main() {
                    clearTimeouts()
                    sortRooms()
                    handleSearch()
                    applyFilters()
                    showCurrentRoomsPage()
                    checkRoomToOpen()
                    self.sendNewMessagesCountToDashboard()

                    // After chat mounted
                    if (!self.chatMountedDone) {
                        self.chatMountedDone = true
                        setTimeout(function () {
                            self.handleChatMounted()
                        })
                    }
                }

                function clearTimeouts() {
                    clearTimeout(self.searchPromise)
                    self.refreshChatPromiseStarted = false
                    clearTimeout(self.refreshChatPromise)
                }

                function sortRooms() {
                    // Ordena os chats pela última mensagem e mensagens por ler
                    const defaultDate = new Date('1994-01-19')
                    self.rooms = self.rooms.sort(function (a, b) {
                        const fixedA = a.fixed || (roomIdActive && roomIdActive === a.roomId) ? 1 : 0
                        const fixedB = b.fixed || (roomIdActive && roomIdActive === b.roomId) ? 1 : 0
                        const lastMessageDateA = a.lastMessageDate ? new Date(a.lastMessageDate) : defaultDate
                        const lastMessageDateB = b.lastMessageDate ? new Date(b.lastMessageDate) : defaultDate
                        return fixedB - fixedA || lastMessageDateB - lastMessageDateA
                    })
                }

                function handleSearch() {
                    if (self.roomsSearchValue && window.isPT) {
                        self.currentRoomsPage = 0
                        self.currentRoomsPageInput = 1
                        filtered = self.rooms.filter(function (item) {
                            return item.roomName.toLowerCase().indexOf(self.roomsSearchValue.toLowerCase()) > -1
                        })
                    } else {
                        filtered = self.rooms
                    }
                }

                function applyFilters() {
                    let tagsFiltersUsed = false

                    if (!self.roomIdToOpen) {
                        let hideRoomsArchived = true
                        let hideRoomsTags = true

                        for (let i = 0; i < self.filtersActive.length; i++) {
                            if (self.filtersActive[i].id === self.filterKeys.newRegisters) {
                                filtered = filtered.filter(function (item) {
                                    return item.roomName.toLowerCase().indexOf(self.newClientKey.toLowerCase()) > -1
                                })
                            }

                            if (self.filtersActive[i].id === self.filterKeys.clientsWithNewMessages) {
                                hideRoomsArchived = false
                                hideRoomsTags = false
                                filtered = filtered.filter(function (item) {
                                    return item.unreadCount > 0
                                })
                            }

                            if (self.filtersActive[i].id === self.filterKeys.clientsWithDraft) {
                                filtered = filtered.filter(function (item) {
                                    return item.draft
                                })
                            }

                            if (self.filtersActive[i].id === self.filterKeys.unansweredClients) {
                                filtered = filtered.filter(function (item) {
                                    return item.lastMessage && item.lastMessage.userId && item.lastMessage.userId !== self.chatUser.chat_id
                                })
                            }

                            if (self.filtersActive[i].id === self.filterKeys.archivedConversations) {
                                hideRoomsArchived = false
                                filtered = filtered.filter(function (item) {
                                    return item.archived
                                })
                            }

                            if (self.filtersActive[i].id === self.filterKeys.moreThanUnreadMessages && self.filtersActive[i].min && self.filtersActive[i].min.value) {
                                hideRoomsArchived = false
                                filtered = filtered.filter(function (item) {
                                    return item.unreadCount >= parseInt(self.filtersActive[i].min.value)
                                })
                            }

                            if (self.filtersActive[i].tag && self.filtersGroupsEnabled) {
                                hideRoomsTags = false
                                tagsFiltersUsed = true
                                self.filteredTagsRooms = JSON.parse(JSON.stringify(filtered))
                                filtered = filtered.filter(function (item) {
                                    return item.tags && item.tags.indexOf(self.filtersActive[i].id) > -1
                                })
                            }
                        }

                        if (self.roomArchivedEnabled && hideRoomsArchived && hideRoomsTags) {
                            filtered = filtered.filter(function (item) {
                                return !item.archived
                            })
                        }

                        if (self.tagsEnabled && hideRoomsTags && self.filtersGroupsEnabled) {
                            if (!tagsFiltersUsed) {
                                self.filteredTagsRooms = JSON.parse(JSON.stringify(filtered))
                            }
                            filtered = filtered.filter(function (item) {
                                return !item.tags || !item.tags.length || !self.filterTagsAvailable.some(a => item.tags.includes(a))
                            })
                        }

                        if (self.filterCategoriesEnabled && !self.archivedConversationsFilterActive) {
                            filtered = filtered.filter(function (item) {
                                return item.category === self.categoryFilterActive
                            })
                        }
                    }
                }

                function showCurrentRoomsPage() {
                    const roomsPerPage = self.roomsPaginationEnabled ? self.initialTotalRooms : filtered.length
                    self.cacheFilteredRooms = sliceIntoChunks(JSON.parse(JSON.stringify(filtered)), roomsPerPage)
                    if (self.roomIdToOpen) {
                        for (let p = 0; p < self.cacheFilteredRooms.length; p++) {
                            const found = self.cacheFilteredRooms[p].find(function (item) {
                                return item.roomId === self.roomIdToOpen
                            })
                            if (found) {
                                self.refreshRoomsPage(p)
                                return true
                            }
                        }
                    }
                    self.refreshRoomsPage(self.currentRoomsPage)

                    function sliceIntoChunks(arr, chunkSize) {
                        const res = []
                        for (let i = 0; i < arr.length; i += chunkSize) {
                            const chunk = arr.slice(i, i + chunkSize)
                            res.push(chunk)
                        }
                        return res
                    }
                }

                function checkRoomToOpen() {
                    setTimeout(function () {
                        if (self.roomIdToOpen && self.roomIdToOpen !== self.getActiveRoomId()) {
                            const roomElement = self.getChatHtmlElement(self.roomIdToOpen)
                            if (roomElement) {
                                self.setRoomOpened()
                                self.setRoomDraftMessageInput(null)
                                roomElement.click()
                            }
                        }
                    })
                }
            },
            changeCurrentRoomsPage: function (event) {
                let newPage = parseInt(event.currentTarget.value)
                if (isNaN(newPage) || newPage < 1) {
                    newPage = 1
                }
                const lastPage = this.cacheFilteredRooms.length
                if (newPage > lastPage) {
                    newPage = lastPage
                }
                this.refreshRoomsPage(newPage - 1)
            },
            nextRoomsPage: function () {
                this.refreshRoomsPage(this.currentRoomsPage + 1)
            },
            previousRoomsPage: function () {
                if (this.currentRoomsPage > 0) {
                    this.refreshRoomsPage(this.currentRoomsPage - 1)
                }
            },
            refreshRoomsPage: function (newPageIndex) {
                if (!this.cacheFilteredRooms.length) {
                    this.clearPageEmployee(newPageIndex)
                    this.currentRoomsPage = 0
                    this.refreshChatRooms([])
                    this.currentRoomsPageInput = this.currentRoomsPage + 1
                    return true
                }
                if (this.cacheFilteredRooms[newPageIndex]) {
                    this.clearPageEmployee(newPageIndex)
                    this.currentRoomsPage = newPageIndex
                    this.refreshChatRooms(this.cacheFilteredRooms[newPageIndex])
                    this.currentRoomsPageInput = this.currentRoomsPage + 1
                }
                this.employeePageToSend = true
            },
            clearPageEmployee: function (newPageIndex) {
                if (this.pagesEmployeesEnabled && (newPageIndex !== this.currentRoomsPage || this.cacheFilteredRooms.length !== this.pagesEmployeesCache)) {
                    this.setPageEmployee(true)
                }
            },
            getPagesEmployees: function () {
                const self = this
                Api.chat.getPagesEmployees(function (response) {
                    if (response.success) {
                        response.data.forEach(function (item) {
                            self.pagesEmployees[item.number] = item.label ? JSON.parse(item.label) : []
                        })
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            setPageEmployee: function (empty) {
                const self = this
                if (this.pagesEmployeesEnabled && !this.roomsSearchValue && window.logName) {
                    const pageNumber = this.currentRoomsPageInput
                    if (empty) {
                        if (this.pagesEmployees[pageNumber]) {
                            const index = this.pagesEmployees[pageNumber].indexOf(window.logName)
                            if (index > -1) {
                                this.pagesEmployees[pageNumber].splice(index, 1)
                                Api.chat.updatePageEmployee({
                                    number: pageNumber,
                                    label: JSON.stringify(this.pagesEmployees[pageNumber])
                                }, function (response) { })
                            }
                        }
                        return true
                    }

                    let hasFilterActive = false
                    if (this.filtersActive && this.filtersActive.length) {
                        for (let f = 0; f < this.filtersActive.length; f++) {
                            if (this.filtersActive[f].id === this.filterKeys.unansweredClients) {
                                hasFilterActive = true
                                break
                            }
                        }
                    }

                    if (this.employeePageToSend && hasFilterActive) {
                        this.pagesEmployeesCache = this.cacheFilteredRooms.length
                        this.employeePageToSend = false
                        if (this.pagesEmployees[pageNumber]) {
                            const index = this.pagesEmployees[pageNumber].indexOf(window.logName)
                            if (index === -1) {
                                this.pagesEmployees[pageNumber].push(window.logName)
                            }
                        } else {
                            this.pagesEmployees[pageNumber] = [window.logName]
                        }
                        Api.chat.updatePageEmployee({
                            number: pageNumber,
                            label: JSON.stringify(this.pagesEmployees[pageNumber])
                        }, function (response) { })
                    }
                }
            },
            sendNewMessagesCountToDashboard: function () {
                const newMessagesCount = this.rooms.reduce((sum, it) => sum + (it.archived ? 0 : it.unreadCount), 0)
                this.refreshFiltersUnreadMessages()
                this.sendEventToDashboard({
                    event: 'new-messages',
                    data: newMessagesCount
                })
            },
            refreshFiltersUnreadMessages: function () {
                const self = this
                if (this.filtersGroupsEnabled) {
                    setTimeout(function () {
                        const tagsUnreadMessages = {
                            'no_tag': 0
                        }
                        self.filteredTagsRooms.forEach(it => {
                            if (it.tags && it.tags.length) {
                                let hasValidTag = false
                                it.tags.forEach(tag => {
                                    if (self.filterTagsAvailable.includes(tag)) {
                                        tagsUnreadMessages[tag] = (tagsUnreadMessages[tag] || 0) + it.unreadCount
                                        hasValidTag = true
                                    }
                                })
                                if (!hasValidTag) {
                                    tagsUnreadMessages['no_tag'] += it.unreadCount
                                }
                            } else {
                                tagsUnreadMessages['no_tag'] += it.unreadCount
                            }
                        })
                        self.filters.forEach((filter, i) => {
                            if (filter.group) {
                                filter.unreadMessages = tagsUnreadMessages[filter.id] || 0
                                self.$set(self.filters, i, filter)
                            }
                        })
                    })
                }
                if (this.filterCategoriesEnabled) {
                    setTimeout(function () {
                        const categoriesUnreadMessages = {}
                        self.rooms.forEach(it => {
                            if (!categoriesUnreadMessages[it.category]) {
                                categoriesUnreadMessages[it.category] = 0
                            }
                            categoriesUnreadMessages[it.category] += it.unreadCount
                        })
                        for (let i = 0; i < self.chatCategories.length; i++) {
                            self.chatCategories[i].unreadMessages = categoriesUnreadMessages[self.chatCategories[i].value] || 0
                            self.$set(self.chatCategories, i, self.chatCategories[i])
                        }
                    })
                }
            },
            openSendMessageToAll: function () {
                this.closeDialogs()
                this.sendMessageToAllVisible = true
                this.messageToAllValue = ''
            },
            confirmSendMessageToAll: function () {
                const self = this
                const list = []
                this.filteredRooms.forEach(function (room) {
                    list.push({
                        chat_id: room.roomId,
                        user_id: self.chatUser.chat_id,
                        type: self.filesTypes.text,
                        message: self.messageToAllValue
                    })
                })

                Api.chat.newMessages(list, function (response) {
                    if (response.success) {
                        self.$alert(
                            self.str['messages_sent'],
                            '',
                            'success',
                            Utils.getAlertOptions()
                        )
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })

                this.sendMessageToAllVisible = false
                this.messageToAllValue = ''
            },
            cancelSendMessageToAll: function () {
                this.sendMessageToAllVisible = false
                this.messageToAllValue = ''
            },
            openFilters: function () {
                this.closeDialogs()
                this.filtersVisible = true
            },
            selectGroupFilter: function (filter) {
                if (!filter.value) {
                    for (let i = 0; i < this.filters.length; i++) {
                        if (this.filters[i].group) {
                            if (this.filters[i].id !== 'all' && this.filters[i].id === filter.id) {
                                this.filters[i].value = true
                            } else {
                                this.filters[i].value = false
                            }
                        }
                    }
                }
                this.confirmFilters()
            },
            selectCategoryFilter: function (category) {
                this.categoryFilterActive = category.value
                this.confirmFilters()
            },
            confirmFilters: function () {
                this.filtersActive = []
                for (let i = 0; i < this.filters.length; i++) {
                    if (this.filters[i].value) {
                        this.filtersActive.push({
                            id: this.filters[i].id,
                            days: this.filters[i].days,
                            min: this.filters[i].min,
                            tag: this.filters[i].tag
                        })
                    }
                }
                this.filtersVisible = false
                this.messageToAllValue = ''
                this.currentRoomsPage = 0
                this.currentRoomsPageInput = 1
                this.applySearchRoomsValue()
            },
            cancelFilters: function () {
                this.resetFilters()
                this.filtersVisible = false
                this.messageToAllValue = ''
                this.applySearchRoomsValue()
            },
            resetFilters: function () {
                this.setDefaultFilters()
                this.filtersActive = []
            },
            closeDialogs: function () {
                this.sendMessageToAllVisible = false
                this.filtersVisible = false
            },
            getRoomOptions: function () {
                const options = []
                const activeRoom = this.getActiveRoom()
                if (activeRoom) {
                    if (this.openDashboardClientEnabled && window.dashboardClientUrl && activeRoom.roomName.indexOf(this.newClientKey) === -1) {
                        options.push({
                            label: 'Abrir cliente no dashboard',
                            value: 'open_dashboard_client'
                        })
                    }
                    if (this.openDashboardRegisterEnabled && activeRoom.roomName.indexOf(this.newClientKey) > -1) {
                        options.push({
                            label: window.strings['open_register'],
                            value: 'open_dashboard_register'
                        })
                    }
                    if (this.openRoomSummaryNotes) {
                        options.push({
                            label: window.strings['view_notes'],
                            value: 'open_summary_notes'
                        })
                    }
                    if (this.IASummaryEnabled) {
                        options.push({
                            label: window.strings['open_summary'],
                            value: 'open_ia_summary'
                        })
                    }
                    if (this.changeRoomNameEnabled) {
                        options.push({
                            label: window.strings['update_name'],
                            value: 'change_room_name'
                        })
                    }
                    if (this.roomFixedEnabled) {
                        options.push({
                            label: activeRoom.fixed ? window.strings['unfix'] : window.strings['fix'],
                            value: 'fix_room'
                        })
                    }
                    if (this.roomArchivedEnabled) {
                        options.push({
                            label: activeRoom.archived ? window.strings['unarchive'] : window.strings['archive'],
                            value: 'archive_room'
                        })
                    }
                    if (this.roomUpdateTagsEnabled) {
                        options.push({
                            label: window.strings['tags'],
                            value: 'update_tags'
                        })
                    }
                    if (this.newCalendarEventEnabled) {
                        options.push({
                            label: window.strings['new_event'],
                            value: 'new_calendar_event'
                        })
                    }
                    if (this.showImagesEnabled) {
                        options.push({
                            label: window.strings['images'],
                            value: 'show_images'
                        })
                    }
                    if (this.deleteRoomEnabled && window.deleteRoomPermission) {
                        options.push({
                            label: window.strings['delete'],
                            value: 'delete_room'
                        })
                    }
                }
                this.roomOptions = options
            },
            onSelectRoomOption: function (option) {
                switch (option) {
                    case 'open_dashboard_client':
                        this.openDashboardClient()
                        break
                    case 'change_room_name':
                        this.updateRoom('name')
                        break
                    case 'fix_room':
                        this.updateRoom('fixed')
                        break
                    case 'archive_room':
                        this.updateRoom('archived')
                        break
                    case 'update_tags':
                        this.openRoomTags()
                        break
                    case 'open_dashboard_register':
                        this.openDashboardRegister()
                        break
                    case 'open_summary_notes':
                        this.openRoomSummaryNotes()
                        break
                    case 'open_ia_summary':
                        this.openIASummary()
                        break
                    case 'new_calendar_event':
                        this.openNewCalendarEventDialog()
                        break
                    case 'show_images':
                        this.openImagesDialog()
                        break
                    case 'delete_room':
                        this.deleteRoom()
                        break
                }
            },
            updateRoom: function (action, data) {
                const self = this
                const roomId = self.getActiveRoomId()
                const room = this.rooms.find(function (room) {
                    return room.roomId === roomId
                })
                if (!room) {
                    return false
                }
                if (action === 'name') {
                    this.$prompt(window.strings['update_name'], room.roomName).then(newName => {
                        self.updateChatRoom({
                            id: room.roomId,
                            name: newName,
                            forceRefresh: true
                        })
                    })
                }
                if (action === 'fixed') {
                    this.updateChatRoom({
                        id: room.roomId,
                        fixed: room.fixed === 0 ? 1 : 0,
                        forceRefresh: true
                    })
                }
                if (action === 'archived') {
                    this.updateChatRoom({
                        id: room.roomId,
                        archived: room.archived === 0 ? 1 : 0
                    })
                }
                if (action === 'tags') {
                    this.updateChatRoom({
                        id: room.roomId,
                        tags: data
                    })
                }
            },
            deleteRoom: function () {
                const self = this
                const roomId = this.getActiveRoomId()
                if (roomId) {
                    this.$confirm(
                        window.strings['are_you_sure_delete'],
                        '',
                        'warning',
                        Utils.getAlertOptions(true, true)
                    ).then(() => {
                        self.$isLoading(true)
                        self.getRoomClient({
                            fields: ['id'],
                            callback: function (client) {
                                if (client && client.id) {
                                    self.closeRoom()
                                    setTimeout(function () {
                                        Api.chat.deleteChat({
                                            id: roomId,
                                            client_id: client.id
                                        }, function (response) {
                                            self.$isLoading(false)
                                            if (response.success) {
                                                self.deleteChat(roomId)
                                            } else {
                                                self.$alert(
                                                    response.message,
                                                    window.strings['warning'],
                                                    'warning',
                                                    Utils.getAlertOptions()
                                                )
                                            }
                                        }) 
                                    }) 
                                }
                            }
                        })
                    }).catch(() => { })
                }
            },
            deleteChat: function (id) {
                if (id) {
                    const rooms = JSON.parse(JSON.stringify(this.rooms))
                    const index = rooms.findIndex(function (rm) {
                        return rm.roomId === id
                    })
                    if (index > -1) {
                        rooms.splice(index, 1)
                        this.rooms = rooms
                        this.applySearchRoomsValue()
                    }
                }
            },
            onMessageActionHandler: function (data) {
                const self = this

                if (data.action.name === 'markAsUnread') {
                    const id = parseInt(data.message._id)
                    const payload = {
                        id: id,
                        chat_id: parseInt(data.roomId),
                        status: this.messageStatus.delivered,
                    }
                    self.markAsUnreadCache = {
                        messageId: id,
                        activeRoom: self.getActiveRoomId()
                    }
                    Api.chat.updateMessage(payload, function (response) {
                        if (response.success) {
                            const messages = JSON.parse(JSON.stringify(self.messages))
                            for (let i = 0; i < messages.length; i++) {
                                if (messages[i]._id === id) {
                                    messages[i] = self.encodeMessages([response.data])[0]
                                    break
                                }
                            }
                            self.refreshChatMessages(messages)
                            self.refreshRoom({
                                markAsUnread: true
                            })
                        } else {
                            self.markAsUnreadCache = null
                        }
                    })
                }
            },
            onToggleRoomsList: function () {
                if (this.roomsSearchValue) {
                    this.resetSearchInput()
                }
                this.clearRoomOpened()
                this.refreshChat()
                this.markAsUnreadCache = null
            },
            onKeydown: function () {
                const self = this
                document.onkeydown = function (evt) {
                    evt = evt || window.event
                    let isEscape = false
                    if ('key' in evt) {
                        isEscape = (evt.key === 'Escape' || evt.key === 'Esc')
                    } else {
                        isEscape = (evt.keyCode === 27)
                    }
                    if (isEscape) {
                        if (!self.getChatPreviewFile()) {
                            if (self.getActiveRoom()) {
                                self.closeRoom()
                            }
                        }
                        return true
                    }
                    if ((evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') && self.getChatPreviewFile()) {
                        return self.changePreviewFile('previous')
                    }
                    if ((evt.key === 'ArrowRight' || evt.key === 'ArrowDown') && self.getChatPreviewFile()) {
                        return self.changePreviewFile('next')
                    }
                }
            },
            changePreviewFile: function (mode) {
                const self = this
                try {
                    if (!this.previewFileOpened) {
                        return false
                    }
                    const currentMessage = this.getChatHtmlElement(this.previewFileOpened._id);
                    if (currentMessage && currentMessage.parentElement) {
                        let element = mode === 'previous' ? currentMessage.parentElement.previousElementSibling : currentMessage.parentElement.nextElementSibling;
                        let foundElement = null;
                        while (element) {
                            const imageContainer = element.querySelector('.vac-message-image-container');
                            const fileContainer = element.querySelector('.vac-file-container');
                            if (imageContainer || fileContainer) {
                                foundElement = element;
                                break;
                            }
                            element = mode === 'previous' ? element.previousElementSibling : element.nextElementSibling;
                        }
                        if (foundElement) {
                            const childImageContainer = foundElement.querySelector('.vac-message-image-container');
                            if (childImageContainer) {
                                childImageContainer.click();
                            } else {
                                const childFileContainer = foundElement.querySelector('.vac-file-container');
                                if (childFileContainer && foundElement.children && foundElement.children[0]) {
                                    let foundMessageId = foundElement.children[0].getAttribute('id')
                                    if (!foundMessageId) {
                                        return false
                                    }
                                    foundMessageId = parseInt(foundMessageId)
                                    const foundMessage = self.messages.find(function (m) {
                                        return m._id === foundMessageId
                                    })
                                    if (foundMessage && foundMessage.type === self.filesTypes.image) {
                                        childFileContainer.click();
                                        if (this.previewFileToOpen && this.previewFileToOpen.promise) {
                                            clearTimeout(this.previewFileToOpen.promise)
                                        }
                                        this.previewFileToOpen = {
                                            element: foundElement
                                        }
                                        this.$isLoading(true)
                                        this.previewFileToOpen.promise = setTimeout(function () {
                                            if (self.previewFileToOpen && self.previewFileToOpen.promise) {
                                                clearTimeout(self.previewFileToOpen.promise)
                                                self.previewFileToOpen = null
                                            }
                                            self.$isLoading(false)
                                        }, 4000)
                                    }
                                }
                            }
                        }
                    }
                } catch { }
            },
            checkOpenPreviewFile: function () {
                const self = this
                try {
                    if (this.previewFileToOpen) {
                        this.$isLoading(false)
                        clearTimeout(this.previewFileToOpen.promise)
                        setTimeout(function () {
                            const childImageContainer = self.previewFileToOpen.element.querySelector('.vac-message-image-container');
                            if (childImageContainer) {
                                childImageContainer.click();
                            }
                            self.previewFileToOpen = null
                        })
                    }
                } catch { }
            },
            onTypingMessage: function (data) {
                if (this.draftEnabled && data && data.message !== null) {
                    this.draftInputValue = data.message
                }
            },
            setRoomDraftMessageInput: function (roomId, draft) {
                const self = this

                if (this.draftEnabled) {

                    if (roomId === null) {
                        this.draftInputValue = ''
                        this.roomDefaultMessage = ''
                        const input = self.getChatHtmlElement('roomTextarea')
                        if (input) {
                            input.value = draft ? draft : ''
                        }
                        return true
                    }

                    this.draftInputValue = ''
                    this.roomDefaultMessage = ''
                    if (draft) {
                        this.roomDefaultMessage = draft
                        this.draftInputValue = draft
                        this.refreshMessageInput()
                    }
                    setTimeout(function () {
                        const input = self.getChatHtmlElement('roomTextarea')
                        if (input) {
                            input.value = draft ? draft : ''
                        }
                    })
                    if (!this.messageInputfocusEventEnabled) {
                        this.messageInputfocusEventEnabled = true
                        const inputElement = this.getChatHtmlElement('roomTextarea')
                        if (inputElement) {
                            inputElement.addEventListener('focusout', function () {
                                const activeRoom = self.getActiveRoom()
                                if (activeRoom && activeRoom.draft !== self.draftInputValue) {
                                    self.updateRoomDraft()
                                }
                            })
                        }
                    }
                }
            },
            refreshMessageInput: function () {
                const self = this
                setTimeout(function () {
                    const input = self.getChatHtmlElement('roomTextarea')
                    if (input) {
                        if (input.value) {
                            const padding = 14
                            input.style.height = (input.scrollHeight - padding) + 'px'
                        } else {
                            input.style.height = ''
                        }
                    }
                })
            },
            updateRoomDraft: function () {
                if (this.draftEnabled && this.roomOpenedId) {
                    const newDraft = this.draftInputValue ? this.draftInputValue : ''
                    this.updateChatRoom({
                        id: this.roomOpenedId,
                        draft: newDraft
                    })
                    for (let i = 0; i < this.rooms.length; i++) {
                        if (this.rooms[i].roomId === this.roomOpenedId) {
                            this.rooms[i].draft = newDraft
                            break
                        }
                    }
                }
            },
            setRoomOpened: function (roomId) {
                if (roomId) {
                    this.roomOpenedId = roomId
                }
                if (this.roomTypingEnabled) {
                    if (this.roomOpenedId) {
                        const newVal = roomId ? 1 : 0
                        this.updateChatRoom({
                            id: this.roomOpenedId,
                            opened: newVal
                        })
                        for (let i = 0; i < this.rooms.length; i++) {
                            if (this.rooms[i].roomId === this.roomOpenedId) {
                                this.rooms[i].opened = newVal
                                break
                            }
                        }
                    }
                }
                if (!roomId) {
                    this.roomOpenedId = null
                }
            },
            clearRoomOpened: function () {
                let valuesToUpdate = false

                if (!this.roomOpenedId) {
                    return false
                }

                if (this.roomTypingEnabled) {
                    valuesToUpdate = {
                        opened: 0
                    }
                }

                if (this.draftEnabled && this.roomOpenedId) {
                    valuesToUpdate = valuesToUpdate ? valuesToUpdate : {}
                    valuesToUpdate.draft = this.draftInputValue ? this.draftInputValue : ''
                }

                if (valuesToUpdate) {
                    for (let i = 0; i < this.rooms.length; i++) {
                        if (this.rooms[i].roomId === this.roomOpenedId) {
                            if ('opened' in valuesToUpdate) {
                                this.rooms[i].opened = valuesToUpdate.opened
                            }
                            if ('draft' in valuesToUpdate) {
                                this.rooms[i].draft = valuesToUpdate.draft
                            }
                            break
                        }
                    }

                    const payload = {
                        id: this.roomOpenedId
                    }
                    if ('opened' in valuesToUpdate) {
                        payload.opened = valuesToUpdate.opened
                    }
                    if ('draft' in valuesToUpdate) {
                        payload.draft = valuesToUpdate.draft
                    }
                    this.updateChatRoom(payload)
                }

                this.summaryCache = null
                this.roomOpenedId = null
                this.resetRoomOpened()
            },
            openPendingFeedback: function () {
                const self = this
                this.$isLoading(true)
                this.getRoomClient({
                    fields: ['id', 'pt_id', 'feedback_day'],
                    callback: function (client) {
                        self.$isLoading(false)
                        self.pendingFeedbackClient = client
                        self.dialogPendingFeedback = true
                    }
                })
            },
            updateClientFeedback: function () {
                const self = this
                self.$isLoading(true)
                Api.pt.updateUser({
                    id: self.pendingFeedbackClient.id,
                    pt_id: self.pendingFeedbackClient.pt_id,
                    feedback_day: self.pendingFeedbackClient.feedback_day
                }, function (response) {
                    if (response.success) {
                        self.closeClientFeedback()
                        self.$isLoading(false)
                    } else {
                        self.$isLoading(false)
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            closeClientFeedback: function () {
                this.dialogPendingFeedback = false
                this.pendingFeedbackClient = null
            },
            openNewCalendarEventDialog: function () {
                this.clearCalendarEventData()
                this.dialogNewCalendarEvent = true
            },
            closeNewCalendarEventDialog: function () {
                this.dialogNewCalendarEvent = false
            },
            clearCalendarEventData: function () {
                for (let i = 0; i < this.newCalendarEventData.length; i++) {
                    this.newCalendarEventData[i].value = ''
                }
            },
            newCalendarEventValidated: function () {
                let dateStart = ''
                let dateEnd = ''
                for (let i = 0; i < this.newCalendarEventData.length; i++) {
                    if (this.newCalendarEventData[i].required && !this.newCalendarEventData[i].value) {
                        return false
                    }
                    if (this.newCalendarEventData[i].id === 'start_date') {
                        dateStart = this.newCalendarEventData[i].value
                        continue
                    }
                    if (this.newCalendarEventData[i].id === 'end_date') {
                        dateEnd = this.newCalendarEventData[i].value
                        continue
                    }
                }
                const startDate = dateStart
                const endDate = dateEnd
                if (startDate > endDate) {
                    return false
                }
                return true
            },
            newCalendarEvent() {
                const self = this
                this.$isLoading(true)
                this.getRoomClient({
                    fields: ['id'],
                    callback: function (client) {
                        const event = {
                            user_id: client.id,
                            title: window.strings['client'] + ' ' + client.id,
                            color: '#8fb9d1',
                            status: 0
                        }
                        let dateStart = ''
                        let hourStart = '00:00'
                        let dateEnd = ''
                        let hourEnd = '23:59'
                        for (let i = 0; i < self.newCalendarEventData.length; i++) {
                            if (self.newCalendarEventData[i].id === 'start_date') {
                                dateStart = self.newCalendarEventData[i].value
                                continue
                            }
                            if (self.newCalendarEventData[i].id === 'end_date') {
                                dateEnd = self.newCalendarEventData[i].value
                                continue
                            }
                            event[self.newCalendarEventData[i].id] = self.newCalendarEventData[i].value
                        }
                        event.start_date = dateStart + ' ' + hourStart
                        event.end_date = dateEnd + ' ' + hourEnd
                        Api.pt.newCalendarItem(event, function (response) {
                            if (response.success) {
                                self.closeNewCalendarEventDialog()
                                self.$isLoading(false)
                            } else {
                                self.$isLoading(false)
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }
                })
            },
            getRoomClient: function (args) {
                const self = this
                const fields = args.fields
                const updateFlags = args.updateFlags
                const callback = args.callback
                const room = this.getActiveRoom()
                if (room && room.users && room.users[0] && room.users[0]._id) {
                    const chatId = parseInt(room.users[0]._id)
                    Api.pt.getUserByChatId({
                        chatId: chatId,
                        ptId: window.chatUserPtId,
                        fields: fields,
                        updateFlags: updateFlags
                    }, function (response) {
                        if (response.success && response.data && response.data[0]) {
                            callback(response.data[0])
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                } else {
                    this.$isLoading(false)
                }
            },
            openImagesDialog: function () {
                const activeRoomId = this.getActiveRoomId()
                if (activeRoomId) {
                    if (this.dialogImagesData && this.dialogImagesData.roomId === activeRoomId) {
                        this.dialogImages = true
                        return true
                    }
                    this.dialogImagesData = null
                    this.getImagesMessages()
                }
            },
            getImagesMessages: function () {
                const self = this
                const activeRoomId = this.getActiveRoomId()
                if (activeRoomId) {
                    const messageId = this.dialogImagesData && this.dialogImagesData.images && this.dialogImagesData.images[this.dialogImagesData.images.length - 1] ? this.dialogImagesData.images[this.dialogImagesData.images.length - 1].id : null
                    const src = this.dialogImagesData ? JSON.parse(JSON.stringify(this.dialogImagesData)) : {
                        roomId: activeRoomId,
                        images: []
                    }
                    if (messageId) {
                        if (src.processing) {
                            return false
                        }
                        src.processing = true
                        this.dialogImagesData = src
                    } else {
                        this.$isLoading(true)
                    }
                    Api.chat.getImagesMessages({
                        user_id: this.chatUser.chat_id,
                        chat_id: activeRoomId,
                        message_id: messageId
                    }, function (response) {
                        if (!messageId) {
                            self.$isLoading(false)
                        }
                        if (response.success) {
                            const items = []
                            response.data.forEach(function (item) {
                                if (item.media) {
                                    self.setCacheMedia(item.chat_id + '_' + item.id, item.media)
                                    item.date = Utils.convertUtcDate(item.date_utc, false, 'DD-MM-YYYY HH:mm')
                                    items.push(item)
                                }
                            })
                            if (!messageId) {
                                self.dialogImages = true
                            }
                            src.images = src.images.concat(items)
                            src.loadMoreDisabled = !items.length ? true : false
                            src.processing = false
                            self.dialogImagesData = src
                        } else {
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }
            },
            closeImagesDialog: function () {
                this.dialogImages = false
            },

            // Utils
            handleChatMounted: function () {
                const ref = this.$refs.chatWindowRef
                if (ref && ref.shadowRoot) {
                    const style = document.createElement('style')
                    style.innerHTML = '.vac-message-file-container .vac-video-container video{max-height:45vh;}.vac-file-container{width:138px!important;height:138px!important;}.vac-message-wrapper .vac-message-image{width:160px;height:160px;}.vac-room-danger{background-color:#ff5252cc!important;}.vac-room-danger .vac-title-container>div{color:#fff;}.vac-audio-player{padding-left:20px;}.vac-svg-button-relative{position:relative;}.audio-velocity{position:absolute;width:28px;text-align:right;right:20px;margin-top:3px;font-size:12px;}.vac-icon-textarea-left .vac-dot-audio-record-time {white-space:nowrap;}.chat-file-loading {width:25px;height:25px;border:3px solid #ddd;border-bottom-color:#1976d2;border-radius:50%;display:inline-block;box-sizing:border-box;animation:rotation 1s linear infinite;} @keyframes rotation {0% {transform:rotate(0deg);}100% {transform:rotate(360deg);}}.chat-window-virtual-keyboard{height:calc(100vh - 185px)!important;}.vac-message-current .vac-menu-list>div:last-child{display:none;}#roomTextarea{background-color:#fff!important;caret-color:#ccc!important;color:#000!important;}.vac-rooms-container .vac-room-item:hover{background:none;}'
                    if (window.isPT) {
                        style.innerHTML += '.vac-room-item{user-select: none;}'
                        if (this.hideRoomUnreadMessagesValueEnabled) {
                            style.innerHTML += '.vac-room-options-container .vac-badge-counter{color:transparent;}'
                        }
                    } else {
                        style.innerHTML += '.vac-image-buttons{display:none;}.vac-col-messages .vac-container-scroll{overflow-x:hidden;}'
                    }
                    if (this.increaseFontSizeEnabled) {
                        style.innerHTML += '.vac-message-wrapper .vac-card-info{font-size:14px;}.vac-message-wrapper .vac-message-card{font-size:16px;}.vac-message-wrapper .vac-text-timestamp{font-size:12px;}.vac-message-wrapper .vac-progress-time{font-size:14px;}.vac-message-wrapper .vac-line-new{font-size:15px;}.vac-textarea{font-size:18px;}'
                    }
                    ref.shadowRoot.appendChild(style)
                    this.handleKeyboard()
                }
            },
            handleKeyboard: function () {
                const self = this
                if (this.virtualKeyboardEnabled) {
                    const input = this.getChatHtmlElement('roomTextarea')
                    if (input) {
                        input.setAttribute('inputmode', 'none')
                        input.addEventListener('focus', function () {
                            self.openKeyboard()
                        })
                    }
                    const outside = this.getChatHtmlElement('messages-list')
                    if (outside) {
                        outside.addEventListener('click', function () {
                            self.closeKeyboard()
                        })
                    }
                }
                if (this.nativeKeyboardEnabled) {
                    window.visualViewport.addEventListener('resize', function (event) {
                        const chatWindow = self.getChatHtmlElement('app-chat-window')
                        if (chatWindow) {
                            chatWindow.style.maxHeight = event.target.height + 'px'
                            self.applyScrollToEnd()
                        }
                    })
                }
            },
            applyScrollToEnd: function () {
                const self = this
                setTimeout(function () {
                    const list = self.getChatHtmlElement('messages-list')
                    if (list) {
                        list.scrollTop = list.scrollHeight
                    }
                }, 200)
            },
            getChatHtmlElement: function (id) {
                const ref = this.$refs.chatWindowRef
                if (ref && ref.shadowRoot) {
                    return ref.shadowRoot.getElementById(id)
                }
                return null
            },
            getChatPreviewFile: function () {
                const ref = this.$refs.chatWindowRef
                if (ref && ref._instance && ref._instance.data && ref._instance.data.previewFile && ref.shadowRoot && ref.shadowRoot.querySelector('.vac-media-preview')) {
                    return ref._instance.data.previewFile
                }
                return null
            },
            getActiveRoomId: function () {
                const ref = this.$refs.chatWindowRef
                if (ref && ref._instance && ref._instance.data && ref._instance.data.room && ref._instance.data.room.roomId) {
                    return parseInt(ref._instance.data.room.roomId)
                }
                return null
            },
            getActiveRoom: function () {
                const ref = this.$refs.chatWindowRef
                if (ref && ref._instance && ref._instance.data && ref._instance.data.room && ref._instance.data.room.roomId) {
                    return ref._instance.data.room
                }
                return null
            },
            openRoom: function (id) {
                const roomIndex = this.rooms.findIndex(function (room) {
                    return room.roomId === id
                })
                if (roomIndex > -1) {
                    this.roomIdToOpen = id
                    this.applySearchRoomsValue()
                } else {
                    this.$alert(
                        this.str['chat_open_room_failed'],
                        '',
                        'warning',
                        Utils.getAlertOptions()
                    )
                }
            },
            getFileBase64: function (file, callback) {
                const self = this
                let type = ''

                if (file.type.indexOf('image') > -1) {
                    type = this.filesTypes.image
                    if (file.type.indexOf('heic') > -1) {
                        convertHeicToJpg()
                    } else {
                        getImageBase64()
                    }
                    clearTimeout(this.uploadImageTimeout)
                    this.uploadImageTimeout = setTimeout(function () {
                        self.$isLoading(false)
                        self.$alert(
                            self.str['invalid_image_format'] + ' ' + file.type,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }, 15000)
                    return true
                }
                if (file.type.indexOf('video') > -1) {
                    if (this.sendVideoEnabled && this.isRoomFeatureAvailable('sendVideoEnabled')) {
                        type = this.filesTypes.video
                        getVideoBase64()
                    } else {
                        this.$alert(
                            this.str['video_upload_not_available'],
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                    return true
                }
                type = this.filesTypes.audio
                getAudioBase64()

                function convertHeicToJpg() {
                    self.$isLoading(true)
                    Api.storage.convertImageHeicToPng(file, function (response) {
                        self.$isLoading(false)
                        if (response.success) {
                            file.localUrl = response.data.png_file
                            getImageBase64()
                        } else {
                            clearTimeout(self.uploadImageTimeout)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }

                function getImageBase64() {
                    const image = new Image()
                    image.src = file.localUrl
                    image.onerror = function () {
                        clearTimeout(self.uploadImageTimeout)
                        self.$alert(
                            self.str['invalid_image_format'] + ' ' + file.type,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                    image.onload = function () {
                        clearTimeout(self.uploadImageTimeout)
                        callback(resizeImage(image), type)
                    }

                    function resizeImage(image) {
                        const canvas = document.createElement('canvas')
                        let width = image.width
                        let height = image.height
                        if (width > height) {
                            if (width > self.imageMaxSize) {
                                height *= self.imageMaxSize / width
                                width = self.imageMaxSize
                            }
                        } else {
                            if (height > self.imageMaxSize) {
                                width *= self.imageMaxSize / height
                                height = self.imageMaxSize
                            }
                        }
                        canvas.width = width
                        canvas.height = height
                        const ctx = canvas.getContext('2d')
                        ctx.drawImage(image, 0, 0, width, height)
                        return canvas.toDataURL()
                    }
                }

                function getVideoBase64() {
                    self.loadingWaitActive = true
                    const reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = function (e) {
                        const videoBase64 = self.encodeVideoBase64(reader.result)
                        Api.storage.sendFile({
                            compressed: true,
                            file: {
                                name: self.filesFolder + '/video_chat_' + (new Date()).getTime() + '.' + file.extension,
                                type: 0,
                                file: videoBase64,
                            }
                        }, function (response) {
                            self.loadingWaitActive = false
                            if (response.success) {
                                const id = response.data.id.toString()
                                self.messagesVideosCache[id] = response.data.file ? response.data.file : videoBase64
                                callback(id, type)
                            } else {
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }
                    reader.onerror = function () {
                        self.loadingWaitActive = false
                    }
                }

                function getAudioBase64() {
                    const reader = new window.FileReader()
                    reader.readAsDataURL(file.blob ? file.blob : file)
                    reader.onloadend = function () {
                        Api.storage.sendFile({
                            file: {
                                name: self.filesFolder + '/audio_chat_' + (new Date()).getTime() + '.webm',
                                type: 3,
                                file: reader.result,
                            }
                        }, function (response) {
                            if (response.success) {
                                callback(response.data.file, type)
                            } else {
                                self.$alert(
                                    response.message,
                                    '',
                                    'warning',
                                    Utils.getAlertOptions()
                                )
                            }
                        })
                    }
                }
            },
            encodeVideoBase64: function (videoBase64) {
                if (videoBase64 && videoBase64.indexOf('data:video/quicktime') > -1) {
                    videoBase64 = videoBase64.replace('data:video/quicktime', 'data:video/mp4') 
                }
                return videoBase64
            },
            openImage: function (base64) {
                if (!window.openedByApp) {
                    const image = new Image();
                    image.src = base64;
                    window.open("").document.write(image.outerHTML);
                }
            },
            getMessageVideo: function (videoId, callback) {
                const self = this
                if (this.messagesVideosCache[videoId]) {
                    return callback()
                }
                Api.storage.getFile(videoId, function (response) {
                    if (response.success && response.data && response.data[0]) {
                        self.messagesVideosCache[response.data[0].id] = response.data[0].file
                        callback()
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            deleteMessageVideo: function (videoId) {
                const self = this
                Api.storage.deleteFile(videoId, function (response) {
                    if (response.success) {
                        delete self.messagesVideosCache[videoId]
                    }
                })
            },
            setDefaultFilters: function () {
                const filters = []
                // filters.push({
                //     id: this.filterKeys.newRegisters,
                //     name: this.str['filter_new_registers'],
                //     days: false,
                //     min: false,
                //     value: false
                // })
                // filters.push({
                //     id: this.filterKeys.clientsWithNewMessages,
                //     name: this.str['filter_clients_with_new_messages'],
                //     days: false,
                //     min: false,
                //     value: false
                // })
                filters.push({
                    id: this.filterKeys.clientsWithDraft,
                    name: this.str['filter_clients_with_draft'],
                    days: false,
                    min: false,
                    value: false
                })
                filters.push({
                    id: this.filterKeys.unansweredClients,
                    name: this.str['filter_unanswered_clients'],
                    days: false,
                    min: false,
                    value: false
                })
                if (this.roomArchivedEnabled) {
                    filters.push({
                        id: this.filterKeys.archivedConversations,
                        name: this.str['filter_archived_conversations'],
                        days: false,
                        min: false,
                        value: false
                    })
                }
                filters.push({
                    id: this.filterKeys.moreThanUnreadMessages,
                    name: this.str['filter_more_than_unread_messages'],
                    days: false,
                    min: {
                        value: ''
                    },
                    value: false
                })
                if (this.tagsEnabled && this.filtersGroupsEnabled) {
                    filters.push({
                        id: 'no_tag',
                        name: window.strings['general'],
                        days: false,
                        min: false,
                        value: false,
                        group: true,
                        unreadMessages: 0
                    })
                    const tagsList = Utils.getUserTags()
                    const filterTagsAvailable = []
                    tagsList.forEach(function (tagItem) {
                        if (tagItem.filter) {
                            filterTagsAvailable.push(tagItem.value)
                            filters.push({
                                tag: true,
                                id: tagItem.value,
                                name: tagItem.chat_tab ? (window.strings[tagItem.chat_tab] ? window.strings[tagItem.chat_tab] : tagItem.chat_tab) : (window.strings[tagItem.label] ? window.strings[tagItem.label] : tagItem.label),
                                days: false,
                                min: false,
                                value: false,
                                group: true,
                                unreadMessages: 0
                            })
                        }
                    })
                    this.filterTagsAvailable = filterTagsAvailable
                }
                this.filters = filters
            },
            validateFilters: function () {
                for (let i = 0; i < this.filters.length; i++) {
                    if (!this.filters[i].value) {
                        continue
                    }
                    if (this.filters[i].days) {
                        if (!(/^\d+$/.test(this.filters[i].days[0]))) {
                            return false
                        }
                        if (!(/^\d+$/.test(this.filters[i].days[1]))) {
                            return false
                        }
                        this.filters[i].days[0] = parseInt(this.filters[i].days[0])
                        this.filters[i].days[1] = parseInt(this.filters[i].days[1])
                        const dayMin = this.filters[i].days[0]
                        const dayMax = this.filters[i].days[1]
                        if (dayMin > dayMax) {
                            return false
                        }
                    }
                    if (this.filters[i].min && !parseInt(this.filters[i].min.value)) {
                        return false
                    }
                }
                return true
            },
            receiveDashboardEvents: function () {
                const self = this
                window.addEventListener('message', function (event) {
                    try {
                        if (event && event.data) {
                            const data = JSON.parse(event.data)

                            if (data && data.event === 'open-chat-client' && data.data) {
                                self.closeRoom()
                                self.resetRooms()
                                self.openChatClient(data.data)
                            }

                            if (data && data.event === 'open-new-chat-client' && data.data) {
                                self.closeRoom()
                                self.resetRooms()
                                if (data.data.register_id) {
                                    self.handleUserChat({
                                        clientRegisterId: data.data.register_id
                                    })
                                } else {
                                    self.handleUserChat({
                                        clientDbId: data.data
                                    })
                                }
                            }

                            if (data && data.event === 'close-room') {
                                self.closeRoom()
                            }

                            if (data && data.event === 'change-chat-visible') {
                                self.chatVisible = data.data
                                self.closeRoom()
                                self.resetRooms()
                                self.refreshChat()
                                self.setPageEmployee(true)
                            }

                            if (data && data.event === 'destroy') {
                                self.destroy()
                            }
                        }
                    } catch { }
                })
            },
            refreshChat: function (forceRefresh) {
                if (!window.socketsEnabled || forceRefresh) {
                    this.getNewMessagesCount()
                    if (this.changeRoomNameEnabled) {
                        this.refreshRoomsNames()
                    }
                    if (this.draftEnabled) {
                        this.refreshRoomsDraftAndOpenedFlag()
                    }
                }
            },
            openChatClient: function (data) {
                const dataChatId = data.chat_id ? data.chat_id : data
                const room = this.rooms.find(function (item) {
                    for (let i = 0; i < item.users.length; i++) {
                        if (item.users[i]._id === dataChatId) {
                            return true
                        }
                    }
                    return false
                })

                if (room) {
                    this.roomNameValidation(room, dataChatId)
                    this.openRoom(room.roomId)
                } else {
                    if (data.id) {
                        return this.handleUserChat({
                            clientDbId: data.id
                        })
                    }

                    if (data.chat_id) {
                        return this.handleUserChat({
                            clientChatId: data.chat_id,
                            clientRegisterId: data.register_id
                        })
                    }

                    if (data.register_id) {
                        return this.handleUserChat({
                            clientRegisterId: data.register_id
                        })
                    }
                }
            },
            roomNameValidation: function (room, chatId) {
                const self = this
                try {
                    if (chatId && room && room.roomName && room.roomName.indexOf(this.newClientKey) > -1) {
                        Api.pt.getUserByChatId({
                            chatId: chatId,
                            ptId: window.ptUserId
                        }, function (response) {
                            if (response.success && response.data && response.data[0]) {
                                const client = response.data[0]
                                self.updateChatRoom({
                                    id: room.roomId,
                                    name: client.id + ' - ' + client.name,
                                    forceRefresh: true
                                })
                            }
                        })
                    }
                } catch { }
            },
            handleUserChat: function (args) {
                const self = this
                const clientDbId = args.clientDbId
                const clientChatId = args.clientChatId
                const clientRegisterId = args.clientRegisterId

                self.$isLoading(true)

                if (clientDbId) {
                    return validatePtClient()
                }

                if (clientChatId) {
                    return validateChatUser({
                        chat_id: clientChatId
                    }, clientRegisterId)
                }

                if (clientRegisterId) {
                    return self.handleChatUserWithRegisterId(
                        clientRegisterId,
                        function (details) {
                            validateChatUser({
                                pt_id: window.ptUserId,
                                name: details.name,
                                email: details.email,
                                password: details.password,
                                chat_id: details.chat_id,
                                chat_token: details.chat_token
                            }, clientRegisterId)
                        }
                    )
                }

                function validatePtClient() {
                    Api.pt.getUser(clientDbId, function (response) {
                        if (response.success) {
                            validateChatUser(response.data[0])
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }

                function validateChatUser(user, registerId) {
                    self.handleRegisterUser({
                        user: user,
                        registerId: registerId,
                        callback: function (registerUser) {
                            self.$isLoading(false)
                            const registerUserId = registerUser.id
                            if (registerUserId) {
                                self.openChat({
                                    chat_id: registerUserId,
                                    users: [self.chatUser.chat_id, registerUserId],
                                    registerId: registerId
                                })
                            }
                        }
                    })
                }
            },
            handleChatUserWithRegisterId: function (registerId, callback) {
                const self = this

                Api.pt.getClientRegister(registerId, function (response) {
                    if (response.success) {
                        const registerUser = response.data[0]
                        callback({
                            email: registerUser.email,
                            name: registerUser.name,
                            password: registerUser.password,
                            chat_id: registerUser.chat_id,
                            chat_token: registerUser.chat_token
                        })
                    } else {
                        self.$isLoading(false)
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            closeRoom: function () {
                try {
                    const ref = this.$refs.chatWindowRef
                    if (this.chatUser && this.chatUser.chat_id && this.getActiveRoomId() &&
                        ref && ref._instance && ref._instance.ctx && ref._instance.ctx.toggleRoomsList
                    ) {
                        ref._instance.ctx.toggleRoomsList()
                    }
                } catch { }
            },
            openNotes: function () {
                const self = this
                if (this.roomUser && this.roomUser.notes) {
                    try {
                        this.getFoods(function () {
                            const notes = JSON.parse(JSON.stringify(self.roomUser.notes))
                            const foodsKeys = ['accepted_foods', 'intolerant_foods', 'unaccepted_foods']
                            const data = {}
                            for (const key in notes) {
                                if (foodsKeys.indexOf(key) > -1) {
                                    data[key] = []
                                    if (notes[key]) {
                                        notes[key].forEach(function (id) {
                                            const name = self.foods[id] ? self.foods[id].trim() : null
                                            if (name && data[key].indexOf(name) === -1) {
                                                data[key].push(name)
                                            }
                                        })
                                    }
                                    data[key] = data[key].sort()
                                } else {
                                    data[key] = notes[key]
                                }
                            }
                            self.roomNotesVisible = {
                                data: data
                            }
                        })
                        return true
                    } catch { }
                    this.roomNotesVisible = {
                        data: null
                    }
                }
            },
            closeNotes: function () {
                this.roomNotesVisible = false
            },
            openDashboardClient: function () {
                try {
                    const self = this
                    const room = this.getActiveRoom()
                    if (room && room.users) {
                        const client = room.users.find(function (r) {
                            return self.chatUser.chat_id !== r._id
                        })
                        if (client && client._id) {
                            Api.chat.getUser(client._id, function (response) {
                                if (response.success) {
                                    if (response.data[0] && (response.data[0].email || response.data[0].id)) {
                                        self.sendEventToDashboard({
                                            event: 'open-client-trigger',
                                            data: {
                                                email: response.data[0].email,
                                                chat_id: response.data[0].id
                                            }
                                        })
                                    } else {
                                        self.$alert(
                                            window.strings['user_not_found'],
                                            '',
                                            'warning',
                                            Utils.getAlertOptions()
                                        )
                                    }
                                } else {
                                    self.$alert(
                                        response.message,
                                        '',
                                        'warning',
                                        Utils.getAlertOptions()
                                    )
                                }
                            })
                        }
                    }
                } catch { }
            },
            openRoomTags: function () {
                const room = this.getActiveRoom()
                if (room) {
                    this.roomUpdateTagsData = {
                        value: room.tags,
                        items: Utils.getUserTags(),
                        searchInput: '',
                    }
                    this.dialogUpdateTags = true
                }
            },
            confirmRoomTags: function () {
                const self = this
                const room = this.getActiveRoom()
                if (room && room.users && room.users[0] && room.users[0]._id) {
                    this.$isLoading(true)
                    const chatId = parseInt(room.users[0]._id)
                    const tags = this.roomUpdateTagsData.value ? this.roomUpdateTagsData.value.join(',') : ''
                    Api.pt.updateUser({
                        chat_id: chatId,
                        pt_id: window.chatUserPtId,
                        tags: tags
                    }, function (response) {
                        if (response.success) {
                            self.updateRoom('tags', tags)
                            self.closeRoomTags()
                            self.$isLoading(false)
                        } else {
                            self.$isLoading(false)
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }
            },
            closeRoomTags: function () {
               this.dialogUpdateTags = false
               this.roomUpdateTagsData = null
            },
            openDashboardRegister: function () {
                try {
                    const self = this
                    const room = this.getActiveRoom()
                    if (room && room.users) {
                        const client = room.users.find(function (r) {
                            return self.chatUser.chat_id !== r._id
                        })
                        if (client && client._id) {
                            self.sendEventToDashboard({
                                event: 'open-client-register',
                                data: client._id
                            })
                        } else {
                            self.$alert(
                                window.strings['common_error'],
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    }
                } catch { }
            },
            openIASummary: function (reset) {
                const self = this
                const activeRoomId = self.getActiveRoomId()
                if (activeRoomId) {
                    if (!reset && this.summaryCache && this.summaryCache[activeRoomId]) {
                        this.dialogIASummaryContent = this.summaryCache[activeRoomId]
                        this.dialogIASummary = true
                        return true
                    }
                    this.$isLoading(true)
                    Api.chat.getIASummary(activeRoomId, function (response) {
                        self.$isLoading(false)
                        if (response.success) {
                            if (!self.summaryCache) {
                                self.summaryCache = {}
                            }
                            self.summaryCache[activeRoomId] = response.data
                            self.dialogIASummaryContent = response.data
                            self.dialogIASummary = true
                        } else {
                            self.$alert(
                                response.message,
                                '',
                                'warning',
                                Utils.getAlertOptions()
                            )
                        }
                    })
                }
            },
            closeIASummary: function () {
                this.dialogIASummary = false
                this.dialogIASummaryContent = null
            },
            sendEventToDashboard: function (data) {
                if (window.dashboardNewPage) {
                    if (window.opener) {
                        window.opener.postMessage(JSON.stringify(data), '*')
                    }
                } else {
                    parent.postMessage(JSON.stringify(data), '*')
                }
            },
            updateChatRoom: function (payload) {
                const self = this
                const forceRefresh = payload.forceRefresh ? true : false
                delete payload.forceRefresh
                Api.chat.updateChat(payload, function (response) {
                    if (response.success) {
                        const rooms = JSON.parse(JSON.stringify(self.rooms))
                        const roomIndex = rooms.findIndex(function (r) {
                            return r.roomId === response.data.id
                        })
                        if (roomIndex > -1) {
                            const activeRoom = self.getActiveRoom()
                            if (payload.name) {
                                rooms[roomIndex].roomName = payload.name
                                if (activeRoom) {
                                    activeRoom.roomName = payload.name
                                }
                            }
                            if (payload.fixed !== undefined) {
                                rooms[roomIndex].fixed = payload.fixed
                                if (activeRoom) {
                                    activeRoom.fixed = payload.fixed
                                }
                            }
                            if (payload.draft !== undefined) {
                                rooms[roomIndex].draft = response.data.draft
                                if (activeRoom) {
                                    activeRoom.draft = response.data.draft
                                }
                            }
                            if (payload.opened !== undefined) {
                                rooms[roomIndex].opened = response.data.opened
                                if (activeRoom) {
                                    activeRoom.opened = response.data.opened
                                }
                            }
                            if (payload.archived !== undefined) {
                                rooms[roomIndex].archived = response.data.archived
                                if (activeRoom) {
                                    activeRoom.archived = response.data.archived
                                }
                            }
                            if (payload.tags !== undefined) {
                                const newTags = response.data.tags ? response.data.tags.split(',') : []
                                rooms[roomIndex].tags = newTags
                                if (activeRoom) {
                                    activeRoom.tags = newTags
                                }
                            }
                            self.rooms = rooms
                            self.applySearchRoomsValue(forceRefresh)
                        }
                        if (payload.callback) {
                            payload.callback()
                        }
                    } else {
                        self.$alert(
                            response.message,
                            '',
                            'warning',
                            Utils.getAlertOptions()
                        )
                    }
                })
            },
            resetRooms: function () {
                const self = this
                this.resetSearchInput()
                this.resetFilters()
                this.resetRoomOpened()
                this.setRoomOpened()
                this.applySearchRoomsValue()
                setTimeout(function () {
                    const scrollElement = self.getChatHtmlElement('rooms-list')
                    if (scrollElement) {
                        scrollElement.scrollTop = 0
                    }
                })
            },
            resetSearchInput: function () {
                this.roomsSearchValue = ''
                this.currentRoomsPage = 0
                this.currentRoomsPageInput = 1
            },
            resetRoomOpened: function () {
                this.roomIdToOpen = null
                this.dialogImagesData = null
                this.roomUser = null
                this.dialogRoomSummaryNotes = null
            },
            getCacheMedia: function (id) {
                if (this.cacheMedia && this.cacheMedia[id]) {
                    return this.cacheMedia[id]
                }
                return null
            },
            setCacheMedia: function (id, media) {
                if (!this.cacheMedia) {
                    this.cacheMedia = {}
                }
                this.cacheMedia[id] = media
            },
            isRoomFeatureAvailable: function (field) {
                if (!window.apiDev && this.roomsNamesFeatures && this.roomsNamesFeatures[field]) {
                    const activeRoom = this.getActiveRoom()
                    if (activeRoom && this.roomsNamesFeatures[field].indexOf(activeRoom.roomName) > -1) {
                        return true
                    }
                    return false
                }
                return true
            },

            // Refresh chat variables
            refreshChatMessages: function (value) {
                try {
                    if (value) {
                        const cloneOriginal = JSON.parse(JSON.stringify(value))
                        const cloneProcessed = JSON.parse(JSON.stringify(cloneOriginal))
                        const checkDisableActions = !window.isPT && cloneOriginal && cloneOriginal[cloneOriginal.length - 1] && this.chatUser && (cloneOriginal[cloneOriginal.length - 1].senderId !== this.chatUser.chat_id || cloneOriginal[cloneOriginal.length - 1].seen) ? true : false
                        for (let i = 0; i < cloneProcessed.length; i++) {
                            cloneProcessed[i]._id = cloneProcessed[i]._id.toString()
                            cloneProcessed[i].chat_id = cloneProcessed[i].chat_id.toString()
                            cloneProcessed[i].senderId = cloneProcessed[i].senderId.toString()
                            if ((checkDisableActions || (!window.isPT && cloneOriginal[i].seen)) && !cloneOriginal[i].disableActions && cloneOriginal[i].senderId === this.chatUser.chat_id) {
                                cloneOriginal[i].disableActions = true
                                cloneProcessed[i].disableActions = true
                            }
                        }
                        this.messages = [...cloneOriginal]
                        this.vueAdvancedChatMessages = [...cloneProcessed]
                    }
                } catch { }
            },
            refreshChatRooms: function (value) {
                try {
                    if (value) {
                        const cloneOriginal = JSON.parse(JSON.stringify(value))
                        const cloneProcessed = JSON.parse(JSON.stringify(cloneOriginal))
                        for (let i = 0; i < cloneProcessed.length; i++) {
                            cloneProcessed[i].roomId = cloneProcessed[i].roomId.toString()
                            if (cloneProcessed[i].lastMessage && cloneProcessed[i].lastMessage.userId) {
                                cloneProcessed[i].lastMessage.userId = cloneProcessed[i].lastMessage.userId.toString()
                            }
                            if (cloneProcessed[i].users) {
                                for (let u = 0; u < cloneProcessed[i].users.length; u++) {
                                    cloneProcessed[i].users[u]._id = cloneProcessed[i].users[u]._id.toString()
                                }
                            }
                        }
                        this.filteredRooms = [...cloneOriginal]
                        this.vueAdvancedChatRooms = [...cloneProcessed]
                    }
                } catch { }
            },
            openKeyboard: function () {
                this.virtualKeyboardVisible = true
                const chatWindow = this.getChatHtmlElement('app-chat-window')
                if (chatWindow) {
                    chatWindow.classList.add('chat-window-virtual-keyboard')
                }
                this.applyScrollToEnd()
            },
            closeKeyboard: function () {
                this.virtualKeyboardVisible = false
                const chatWindow = this.getChatHtmlElement('app-chat-window')
                if (chatWindow) {
                    chatWindow.classList.remove('chat-window-virtual-keyboard')
                }
            },
            onKeyboardPress: function (value) {
                const input = this.getChatHtmlElement('roomTextarea')
                let cursorPosition = 0
                if (input.selectionStart || input.selectionStart === 0) {
                    cursorPosition = input.selectionStart
                }
                if (input) {
                    let refreshInput = false
                    let newCursorPosition = false
                    switch (value) {
                        case '{bksp}':
                            input.value = input.value.slice(0, cursorPosition - 1) + input.value.slice(cursorPosition)
                            refreshInput = true
                            newCursorPosition = cursorPosition - 1
                            break;
                        case '{done}':
                            this.closeKeyboard()
                            break;
                        case '{enter}':
                            input.value = input.value.slice(0, cursorPosition) + '\n' + input.value.slice(cursorPosition)
                            refreshInput = true
                            newCursorPosition = cursorPosition + 1
                            break;
                        default:
                            input.value = input.value.slice(0, cursorPosition) + value + input.value.slice(cursorPosition)
                            refreshInput = true
                            newCursorPosition = cursorPosition + value.length
                            break;
                    }
                    if (refreshInput) {
                        input.dispatchEvent(new Event('input'))
                        this.refreshMessageInput()
                    }
                    if (newCursorPosition !== false) {
                        if (input.createTextRange) {
                            const range = input.createTextRange()
                            range.move('character', newCursorPosition)
                            range.select()
                        } else {
                            if (input.selectionStart) {
                                input.focus()
                                input.setSelectionRange(newCursorPosition, newCursorPosition)
                            } else {
                                input.focus()
                            }
                        }
                    }
                }
            },
            handleFocus: function () {
                if (this.focusRefreshChatEnabled) {
                    this.refreshChat(true)
                }
            },
            destroy: function () {
                clearTimeout(newMessagesPromise)
                clearTimeout(roomNamesPromise)
                clearTimeout(newRoomMessagesPromise)
                this.setPageEmployee(true)
                if (this.focusRefreshChatEnabled) {
                    window.removeEventListener('focus', this.handleFocus)
                }
            },
        },
        mounted: function () {
            if (this.focusRefreshChatEnabled) {
                window.addEventListener('focus', this.handleFocus)
            }
        },
        beforeDestroy: function () {
            this.destroy()
        },
    }
</script>